import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
    LayoutDashboard, Users, KanbanSquare, CheckSquare, Wrench, LogOut, X,
    Plus, Download, Search, Tag, Trash2, Mail, Building2, Phone, CalendarClock, Clock, Video, FileText, DollarSign, ExternalLink, RefreshCw, Ban, ScrollText, Copy, Send, PenSquare,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (n) => `$${(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// ---------- Auth Gate ----------
export default function AdminCRM() {
    const { user, loading, login, logout } = useAuth();

    if (loading) {
        return <FullscreenMessage title="Loading…" />;
    }
    if (!user) {
        return (
            <FullscreenMessage
                title="Sign in to CRM"
                body="Only admin users can access the CRM. Sign in with your Google account."
                cta={
                    <button
                        onClick={() => login("/admin")}
                        data-testid="admin-login-btn"
                        className="px-8 py-3 bg-[#0c0f3d] text-[#fbf9f8] text-xs uppercase tracking-[0.2em] hover:bg-[#e47a6a]"
                    >
                        Continue with Google
                    </button>
                }
            />
        );
    }
    if (user.role !== "admin") {
        return (
            <FullscreenMessage
                title="Not authorized"
                body={`This CRM is admin-only. Signed in as ${user.email}.`}
                cta={
                    <button onClick={logout} className="px-8 py-3 border border-[#0c0f3d] text-[#0c0f3d] text-xs uppercase tracking-[0.2em] hover:bg-[#0c0f3d] hover:text-[#fbf9f8]">
                        Sign out
                    </button>
                }
            />
        );
    }
    return <AdminShell user={user} logout={logout} />;
}

function FullscreenMessage({ title, body, cta }) {
    return (
        <main className="min-h-screen bg-[#fbf9f8] flex items-center justify-center px-6" data-testid="admin-gate">
            <div className="max-w-[480px] w-full text-center">
                <h1 className="font-serif text-4xl md:text-5xl text-[#0c0f3d]">{title}</h1>
                {body && <p className="font-sans text-sm text-[#0c0f3d]/70 mt-6 leading-[1.7]">{body}</p>}
                {cta && <div className="mt-10">{cta}</div>}
            </div>
        </main>
    );
}

// ---------- Shell ----------
function AdminShell({ user, logout }) {
    const [tab, setTab] = useState("dashboard");

    const tabs = [
        { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
        { id: "contacts", label: "Contacts", icon: Users },
        { id: "pipeline", label: "Pipeline", icon: KanbanSquare },
        { id: "tasks", label: "Tasks", icon: CheckSquare },
        { id: "bookings", label: "Bookings", icon: CalendarClock },
        { id: "proposals", label: "Proposals", icon: ScrollText },
        { id: "templates", label: "Templates", icon: PenSquare },
        { id: "invoices", label: "Invoices", icon: FileText },
        { id: "services", label: "Services", icon: Wrench },
    ];

    return (
        <div className="min-h-screen bg-[#fbf9f8] flex" data-testid="admin-crm">
            {/* Sidebar */}
            <aside className="w-60 bg-[#0c0f3d] text-[#fbf9f8] flex flex-col fixed h-screen">
                <div className="px-6 py-6 border-b border-white/10">
                    <p className="font-serif text-xl">
                        The Connection <span className="italic text-[#ffb5ab]">Guru</span><sup className="text-[9px] ml-0.5 font-sans not-italic text-[#fbf9f8]/60">&reg;</sup>
                    </p>
                    <p className="font-sans text-[10px] uppercase tracking-[0.3em] text-[#e3c7b9] mt-1">CRM</p>
                </div>
                <nav className="flex-1 px-3 py-6 space-y-1">
                    {tabs.map((t) => (
                        <button
                            key={t.id}
                            onClick={() => setTab(t.id)}
                            data-testid={`admin-tab-${t.id}`}
                            className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm font-sans transition-colors rounded-sm ${
                                tab === t.id ? "bg-[#e47a6a] text-white" : "text-[#fbf9f8]/75 hover:bg-white/5 hover:text-white"
                            }`}
                        >
                            <t.icon size={16} strokeWidth={1.5} />
                            {t.label}
                        </button>
                    ))}
                </nav>
                <div className="px-4 py-4 border-t border-white/10">
                    <p className="font-sans text-xs text-[#fbf9f8]/60 mb-2">Signed in as</p>
                    <p className="font-sans text-sm truncate">{user.email}</p>
                    <button
                        onClick={logout}
                        data-testid="admin-logout-btn"
                        className="mt-3 w-full flex items-center gap-2 text-xs text-[#e3c7b9] hover:text-white transition-colors"
                    >
                        <LogOut size={12} /> Sign out
                    </button>
                </div>
            </aside>

            {/* Main */}
            <main className="flex-1 ml-60 p-8 md:p-12">
                {tab === "dashboard" && <DashboardPanel />}
                {tab === "contacts" && <ContactsPanel />}
                {tab === "pipeline" && <PipelinePanel />}
                {tab === "tasks" && <TasksPanel />}
                {tab === "bookings" && <BookingsPanel />}
                {tab === "proposals" && <ProposalsPanel />}
                {tab === "templates" && <ContractTemplatesPanel />}
                {tab === "invoices" && <InvoicesPanel />}
                {tab === "services" && <ServicesPanel />}
            </main>
        </div>
    );
}

// ---------- Dashboard ----------
function DashboardPanel() {
    const [data, setData] = useState(null);

    useEffect(() => {
        axios.get(`${API}/admin/dashboard`, { withCredentials: true })
            .then((r) => setData(r.data))
            .catch(() => toast.error("Could not load dashboard."));
    }, []);

    if (!data) return <p className="font-serif italic text-2xl text-[#0c0f3d]">Loading…</p>;

    const stats = [
        { label: "Total contacts", value: data.total_contacts, testId: "stat-contacts" },
        { label: "Confirmed bookings", value: data.total_confirmed_bookings || 0, testId: "stat-bookings" },
        { label: "Paid orders", value: data.total_paid_orders, testId: "stat-orders" },
        { label: "Total revenue", value: fmtMoney(data.total_revenue), testId: "stat-revenue" },
        { label: "Outstanding", value: fmtMoney((data.outstanding_invoices || 0)), testId: "stat-outstanding" },
        { label: "Open tasks", value: data.open_tasks, testId: "stat-tasks" },
    ];

    return (
        <div data-testid="admin-dashboard-panel">
            <PanelHeader eyebrow="CRM" title="Dashboard" />
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mt-8">
                {stats.map((s) => (
                    <div key={s.label} data-testid={s.testId} className="bg-white border border-[#e3c7b9] p-5 rounded-sm">
                        <p className="font-sans text-[10px] uppercase tracking-[0.25em] text-[#6d91ad]">{s.label}</p>
                        <p className="font-serif text-3xl text-[#0c0f3d] mt-3">{s.value}</p>
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-10">
                <SectionCard title="Upcoming bookings" testId="dash-upcoming-bookings">
                    {(!data.upcoming_bookings || data.upcoming_bookings.length === 0) ? (
                        <EmptyMsg>No upcoming bookings.</EmptyMsg>
                    ) : (
                        data.upcoming_bookings.map((b) => {
                            const dt = new Date(b.start_iso);
                            return (
                                <div key={b.id} className="py-3 border-b border-[#e3c7b9] last:border-0">
                                    <p className="font-sans text-sm text-[#0c0f3d]">{b.consultation_name}</p>
                                    <p className="font-sans text-xs text-[#0c0f3d]/60 mt-1">
                                        {b.client_name} · {dt.toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZone: "America/New_York" })} ET
                                    </p>
                                </div>
                            );
                        })
                    )}
                </SectionCard>
                <SectionCard title="Due today" testId="dash-tasks-today">
                    {data.tasks_due_today.length === 0 ? (
                        <EmptyMsg>All caught up.</EmptyMsg>
                    ) : (
                        data.tasks_due_today.map((t) => (
                            <div key={t.id} className="py-3 border-b border-[#e3c7b9] last:border-0">
                                <p className="font-sans text-sm text-[#0c0f3d]">{t.title}</p>
                                {t.contact_email && <p className="font-sans text-xs text-[#0c0f3d]/60 mt-1">{t.contact_email}</p>}
                            </div>
                        ))
                    )}
                </SectionCard>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
                <SectionCard title="Recent inquiries" testId="dash-recent-inquiries">
                    {data.recent_inquiries.length === 0 ? <EmptyMsg>No inquiries yet.</EmptyMsg> : data.recent_inquiries.map((i) => (
                        <div key={i.id} className="py-3 border-b border-[#e3c7b9] last:border-0">
                            <p className="font-sans text-sm text-[#0c0f3d]">{i.name || "Anonymous"}</p>
                            <p className="font-sans text-xs text-[#0c0f3d]/60">{i.email} · {i.organization || "—"}</p>
                        </div>
                    ))}
                </SectionCard>
                <SectionCard title="Recent orders" testId="dash-recent-orders">
                    {data.recent_orders.length === 0 ? <EmptyMsg>No orders yet.</EmptyMsg> : data.recent_orders.map((o) => (
                        <div key={o.id} className="py-3 border-b border-[#e3c7b9] last:border-0">
                            <p className="font-sans text-sm text-[#0c0f3d]">{o.product_name}</p>
                            <p className="font-sans text-xs text-[#0c0f3d]/60">{o.email} · {fmtMoney(o.total)}</p>
                        </div>
                    ))}
                </SectionCard>
            </div>
        </div>
    );
}

// ---------- Contacts ----------
function ContactsPanel() {
    const [rows, setRows] = useState([]);
    const [q, setQ] = useState("");
    const [selected, setSelected] = useState(null);

    const load = useCallback(async () => {
        const params = q ? { q } : {};
        try {
            const { data } = await axios.get(`${API}/admin/contacts`, { params, withCredentials: true });
            setRows(data.contacts || []);
        } catch {
            toast.error("Could not load contacts.");
        }
    }, [q]);

    useEffect(() => { load(); }, [load]);

    const exportCsv = () => {
        window.location.href = `${API}/admin/contacts/export.csv`;
    };

    return (
        <div data-testid="admin-contacts-panel">
            <PanelHeader eyebrow="CRM" title="Contacts" />
            <div className="flex items-center gap-3 mt-8">
                <div className="relative flex-1 max-w-md">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0c0f3d]/50" />
                    <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search email, name, organization…"
                        data-testid="contacts-search-input"
                        className="w-full pl-10 pr-4 py-2.5 border border-[#e3c7b9] bg-white text-sm outline-none focus:border-[#0c0f3d]"
                    />
                </div>
                <button
                    onClick={exportCsv}
                    data-testid="contacts-export-btn"
                    className="inline-flex items-center gap-2 px-5 py-2.5 border border-[#0c0f3d] text-[#0c0f3d] text-xs uppercase tracking-[0.2em] hover:bg-[#0c0f3d] hover:text-white"
                >
                    <Download size={14} /> Export CSV
                </button>
            </div>

            <div className="mt-6 bg-white border border-[#e3c7b9]">
                <table className="w-full">
                    <thead className="bg-[#e3c7b9]/40 text-left">
                        <tr>
                            <Th>Contact</Th>
                            <Th>Organization</Th>
                            <Th>Stage</Th>
                            <Th>Sources</Th>
                            <Th>Tags</Th>
                            <Th>Last activity</Th>
                        </tr>
                    </thead>
                    <tbody data-testid="contacts-table-body">
                        {rows.length === 0 ? (
                            <tr><td colSpan={6} className="text-center py-12 font-serif italic text-[#0c0f3d]/60">No contacts yet.</td></tr>
                        ) : rows.map((r) => (
                            <tr
                                key={r.email}
                                onClick={() => setSelected(r.email)}
                                data-testid={`contact-row-${r.email}`}
                                className="cursor-pointer hover:bg-[#e3c7b9]/20 border-t border-[#e3c7b9]"
                            >
                                <Td>
                                    <p className="font-sans text-sm text-[#0c0f3d]">{r.name || "—"}</p>
                                    <p className="font-sans text-xs text-[#0c0f3d]/60">{r.email}</p>
                                </Td>
                                <Td>{r.organization || "—"}</Td>
                                <Td><StagePill stage={r.stage} /></Td>
                                <Td>
                                    <div className="flex gap-1 flex-wrap">
                                        {(r.sources || []).map((s) => (
                                            <span key={s} className="text-[10px] uppercase tracking-[0.15em] bg-[#0c0f3d]/5 text-[#0c0f3d] px-2 py-0.5">{s}</span>
                                        ))}
                                    </div>
                                </Td>
                                <Td>
                                    <div className="flex gap-1 flex-wrap">
                                        {(r.tags || []).map((t) => (
                                            <span key={t} className="text-[10px] bg-[#e47a6a]/20 text-[#e47a6a] px-2 py-0.5">{t}</span>
                                        ))}
                                    </div>
                                </Td>
                                <Td>
                                    <span className="font-sans text-xs text-[#0c0f3d]/60">{r.last_activity ? r.last_activity.slice(0, 10) : "—"}</span>
                                </Td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {selected && <ContactDrawer email={selected} onClose={() => { setSelected(null); load(); }} />}
        </div>
    );
}

function ContactDrawer({ email, onClose }) {
    const [c, setC] = useState(null);
    const [note, setNote] = useState("");
    const [tagInput, setTagInput] = useState("");

    const load = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/admin/contacts/${encodeURIComponent(email)}`, { withCredentials: true });
            setC(data);
        } catch {
            toast.error("Could not load contact.");
        }
    }, [email]);

    useEffect(() => { load(); }, [load]);

    const addNote = async () => {
        if (!note.trim()) return;
        await axios.post(`${API}/admin/contacts/${encodeURIComponent(email)}/notes`, { body: note }, { withCredentials: true });
        setNote("");
        load();
    };
    const setStage = async (stage) => {
        await axios.put(`${API}/admin/contacts/${encodeURIComponent(email)}/stage`, { stage }, { withCredentials: true });
        toast.success(`Stage → ${stage}`);
        load();
    };
    const addTag = async () => {
        if (!tagInput.trim()) return;
        const tags = [...(c.tags || []), tagInput.trim()];
        await axios.put(`${API}/admin/contacts/${encodeURIComponent(email)}/tags`, { tags }, { withCredentials: true });
        setTagInput("");
        load();
    };
    const removeTag = async (t) => {
        const tags = (c.tags || []).filter((x) => x !== t);
        await axios.put(`${API}/admin/contacts/${encodeURIComponent(email)}/tags`, { tags }, { withCredentials: true });
        load();
    };

    if (!c) return null;
    const s = c.sources || {};

    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={onClose} data-testid="contact-drawer">
            <div onClick={(e) => e.stopPropagation()} className="w-full max-w-[720px] h-full bg-white overflow-y-auto">
                <div className="sticky top-0 bg-white border-b border-[#e3c7b9] px-8 py-5 flex items-start justify-between">
                    <div>
                        <p className="font-sans text-[10px] uppercase tracking-[0.3em] text-[#e47a6a]">Contact</p>
                        <h2 className="font-serif text-3xl text-[#0c0f3d] mt-1">{c.name || c.email}</h2>
                        <div className="mt-3 flex items-center gap-4 text-xs text-[#0c0f3d]/70">
                            <span className="inline-flex items-center gap-1.5"><Mail size={12} /> {c.email}</span>
                            {c.organization && <span className="inline-flex items-center gap-1.5"><Building2 size={12} /> {c.organization}</span>}
                            {c.phone && <span className="inline-flex items-center gap-1.5"><Phone size={12} /> {c.phone}</span>}
                        </div>
                    </div>
                    <button onClick={onClose} data-testid="drawer-close-btn" className="p-2 hover:bg-[#e3c7b9]/40"><X size={20} /></button>
                </div>

                <div className="p-8 space-y-8">
                    {/* Stage */}
                    <div>
                        <p className="font-sans text-[10px] uppercase tracking-[0.25em] text-[#6d91ad] mb-3">Stage</p>
                        <div className="flex flex-wrap gap-2">
                            {(["New Lead", "Discovery Call", "Proposal Sent", "Signed", "Client", "Complete", "Archived"]).map((st) => (
                                <button
                                    key={st}
                                    onClick={() => setStage(st)}
                                    data-testid={`stage-btn-${st.replace(/ /g, "-")}`}
                                    className={`text-xs px-3 py-1.5 border ${c.stage === st ? "bg-[#0c0f3d] text-white border-[#0c0f3d]" : "text-[#0c0f3d] border-[#0c0f3d]/30 hover:border-[#0c0f3d]"}`}
                                >
                                    {st}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Lifetime value */}
                    <div className="grid grid-cols-2 gap-4">
                        <MiniStat label="Orders" value={c.order_count} />
                        <MiniStat label="Lifetime value" value={fmtMoney((c.lifetime_value || 0))} />
                    </div>

                    {/* Tags */}
                    <div>
                        <p className="font-sans text-[10px] uppercase tracking-[0.25em] text-[#6d91ad] mb-3">Tags</p>
                        <div className="flex flex-wrap gap-2 mb-3">
                            {(c.tags || []).map((t) => (
                                <span key={t} className="inline-flex items-center gap-1.5 text-xs bg-[#e47a6a]/15 text-[#e47a6a] px-3 py-1.5">
                                    <Tag size={10} /> {t}
                                    <button onClick={() => removeTag(t)} data-testid={`tag-remove-${t}`}><X size={10} /></button>
                                </span>
                            ))}
                        </div>
                        <div className="flex gap-2">
                            <input value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addTag()} placeholder="Add tag…" data-testid="tag-input" className="flex-1 px-3 py-2 border border-[#e3c7b9] text-sm outline-none focus:border-[#0c0f3d]" />
                            <button onClick={addTag} data-testid="tag-add-btn" className="px-4 py-2 bg-[#0c0f3d] text-white text-xs uppercase tracking-[0.2em]"><Plus size={12} /></button>
                        </div>
                    </div>

                    {/* Notes */}
                    <div>
                        <p className="font-sans text-[10px] uppercase tracking-[0.25em] text-[#6d91ad] mb-3">Notes</p>
                        <div className="space-y-2">
                            <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note…" rows={3} data-testid="note-input" className="w-full px-3 py-2 border border-[#e3c7b9] text-sm outline-none focus:border-[#0c0f3d] resize-none" />
                            <button onClick={addNote} data-testid="note-save-btn" className="px-5 py-2 bg-[#0c0f3d] text-white text-xs uppercase tracking-[0.2em]">Add Note</button>
                        </div>
                        <div className="mt-4 space-y-3" data-testid="notes-list">
                            {(c.notes || []).map((n) => (
                                <div key={n.id} className="p-4 bg-[#fbf9f8] border-l-2 border-[#e47a6a]">
                                    <p className="font-sans text-sm text-[#0c0f3d] whitespace-pre-wrap">{n.body}</p>
                                    <p className="font-sans text-[10px] uppercase tracking-[0.2em] text-[#0c0f3d]/50 mt-2">{n.author} · {n.created_at?.slice(0, 10)}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* History */}
                    <div>
                        <p className="font-sans text-[10px] uppercase tracking-[0.25em] text-[#6d91ad] mb-3">Activity history</p>
                        <div className="space-y-4">
                            {(s.inquiries || []).map((i) => <HistoryItem key={i.id} kind="Inquiry" title={i.organization || i.email} body={i.message} date={i.created_at} />)}
                            {(s.orders || []).map((o) => <HistoryItem key={o.id} kind={`Order · ${fmtMoney(o.total)}`} title={o.product_name} body={`${o.payment_status} · ${o.size || ""} ${o.color || ""}`.trim()} date={o.created_at} />)}
                            {(s.bookings || []).map((b) => <HistoryItem key={b.id} kind={`Booking · ${b.status}`} title={b.consultation_name} body={new Date(b.start_iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short", timeZone: "America/New_York" }) + " ET"} date={b.created_at} />)}
                            {(s.prayers || []).map((p) => <HistoryItem key={p.id} kind="Prayer" title="Prayer request" body={p.keep_private ? "(private)" : p.request} date={p.created_at} />)}
                            {(s.waitlist || []).map((w) => <HistoryItem key={w.id} kind="Waitlist" title={`WMWE ${w.tier_interest || w.tier || "membership"} interest`} body="" date={w.created_at} />)}
                            {(!s.inquiries?.length && !s.orders?.length && !s.prayers?.length && !s.waitlist?.length && !s.bookings?.length) && <EmptyMsg>No history recorded yet.</EmptyMsg>}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function HistoryItem({ kind, title, body, date }) {
    return (
        <div className="border-l-2 border-[#6d91ad] pl-4 py-1">
            <div className="flex items-center gap-3">
                <span className="font-sans text-[10px] uppercase tracking-[0.25em] text-[#6d91ad]">{kind}</span>
                <span className="font-sans text-[10px] text-[#0c0f3d]/50">{date?.slice(0, 10)}</span>
            </div>
            <p className="font-serif text-lg text-[#0c0f3d] mt-1">{title}</p>
            {body && <p className="font-sans text-sm text-[#0c0f3d]/70 mt-1">{body}</p>}
        </div>
    );
}

// ---------- Pipeline ----------
function PipelinePanel() {
    const [rows, setRows] = useState([]);
    const [stages, setStages] = useState([]);

    const load = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/admin/contacts`, { withCredentials: true });
            setRows(data.contacts || []);
            setStages(data.stages || []);
        } catch {
            toast.error("Could not load pipeline.");
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const move = async (email, stage) => {
        await axios.put(`${API}/admin/contacts/${encodeURIComponent(email)}/stage`, { stage }, { withCredentials: true });
        load();
    };

    return (
        <div data-testid="admin-pipeline-panel">
            <PanelHeader eyebrow="CRM" title="Pipeline" />
            <div className="mt-8 flex items-start justify-between gap-4 flex-wrap">
                <p className="font-serif text-lg text-[#0c0f3d]/70 max-w-[560px]">Contacts auto-advance when you send proposals, invoices, or receive payments. Drag manually with the dropdown anytime.</p>
                <button
                    onClick={async () => {
                        try {
                            const { data } = await axios.post(`${API}/admin/pipeline/backfill`, {}, { withCredentials: true });
                            if (data.count > 0) toast.success(`Advanced ${data.count} contact${data.count === 1 ? "" : "s"} based on history.`);
                            else toast.success("All contacts are already at their correct stage.");
                            load();
                        } catch { toast.error("Could not recompute stages."); }
                    }}
                    data-testid="pipeline-recompute-btn"
                    className="px-4 py-2 border border-[#0c0f3d] text-[#0c0f3d] text-xs uppercase tracking-[0.2em] hover:bg-[#0c0f3d] hover:text-white flex items-center gap-2"
                ><RefreshCw size={12} /> Recompute stages</button>
            </div>
            <div className="mt-6 overflow-x-auto">
                <div className="flex gap-4 min-w-max pb-4">
                    {stages.map((st) => {
                        const items = rows.filter((r) => r.stage === st);
                        return (
                            <div key={st} className="w-72 bg-[#e3c7b9]/30 rounded-sm p-3" data-testid={`pipeline-col-${st.replace(/ /g, "-")}`}>
                                <div className="flex items-center justify-between px-2 py-1">
                                    <p className="font-sans text-[10px] uppercase tracking-[0.25em] text-[#0c0f3d]">{st}</p>
                                    <span className="font-sans text-xs text-[#0c0f3d]/60">{items.length}</span>
                                </div>
                                <div className="mt-3 space-y-2">
                                    {items.map((r) => (
                                        <div key={r.email} className="bg-white p-3 border border-[#e3c7b9]" data-testid={`pipeline-card-${r.email}`}>
                                            <p className="font-sans text-sm text-[#0c0f3d]">{r.name || r.email}</p>
                                            <p className="font-sans text-xs text-[#0c0f3d]/60 mt-0.5">{r.organization || r.email}</p>
                                            <select
                                                value={r.stage}
                                                onChange={(e) => move(r.email, e.target.value)}
                                                data-testid={`pipeline-move-${r.email}`}
                                                className="mt-2 w-full text-xs border border-[#0c0f3d]/20 px-2 py-1 bg-white"
                                            >
                                                {stages.map((s) => <option key={s} value={s}>{s}</option>)}
                                            </select>
                                        </div>
                                    ))}
                                    {items.length === 0 && <p className="font-serif italic text-xs text-[#0c0f3d]/50 px-2 py-4">Empty</p>}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

// ---------- Tasks ----------
function TasksPanel() {
    const [tasks, setTasks] = useState([]);
    const [filter, setFilter] = useState("open");
    const [showForm, setShowForm] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/admin/tasks`, { params: { filter }, withCredentials: true });
            setTasks(data);
        } catch {
            toast.error("Could not load tasks.");
        }
    }, [filter]);
    useEffect(() => { load(); }, [load]);

    const toggle = async (t) => {
        await axios.put(`${API}/admin/tasks/${t.id}`, { completed: !t.completed }, { withCredentials: true });
        load();
    };
    const remove = async (id) => {
        await axios.delete(`${API}/admin/tasks/${id}`, { withCredentials: true });
        load();
    };

    return (
        <div data-testid="admin-tasks-panel">
            <PanelHeader eyebrow="CRM" title="Tasks" />
            <div className="flex items-center justify-between mt-8">
                <div className="flex gap-2">
                    {["open", "completed", "all"].map((f) => (
                        <button
                            key={f}
                            onClick={() => setFilter(f === "all" ? "" : f)}
                            data-testid={`tasks-filter-${f}`}
                            className={`px-4 py-2 text-xs uppercase tracking-[0.2em] border ${
                                (filter || "all") === f
                                    ? "bg-[#0c0f3d] text-white border-[#0c0f3d]"
                                    : "text-[#0c0f3d] border-[#0c0f3d]/30 hover:border-[#0c0f3d]"
                            }`}
                        >
                            {f}
                        </button>
                    ))}
                </div>
                <button
                    onClick={() => setShowForm(true)}
                    data-testid="tasks-new-btn"
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0c0f3d] text-white text-xs uppercase tracking-[0.2em] hover:bg-[#e47a6a]"
                >
                    <Plus size={14} /> New task
                </button>
            </div>

            {showForm && <TaskForm onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}

            <div className="mt-6 space-y-2" data-testid="tasks-list">
                {tasks.length === 0 ? (
                    <p className="text-center py-12 font-serif italic text-[#0c0f3d]/60">No tasks.</p>
                ) : tasks.map((t) => (
                    <div key={t.id} className="bg-white border border-[#e3c7b9] p-4 flex items-start gap-3" data-testid={`task-row-${t.id}`}>
                        <input
                            type="checkbox"
                            checked={t.completed}
                            onChange={() => toggle(t)}
                            data-testid={`task-toggle-${t.id}`}
                            className="mt-1 w-4 h-4"
                        />
                        <div className="flex-1">
                            <p className={`font-sans text-sm ${t.completed ? "line-through text-[#0c0f3d]/50" : "text-[#0c0f3d]"}`}>{t.title}</p>
                            <div className="mt-1 flex items-center gap-3 text-xs text-[#0c0f3d]/60">
                                {t.due_date && <span>Due {t.due_date.slice(0, 10)}</span>}
                                {t.contact_email && <span>· {t.contact_email}</span>}
                            </div>
                            {t.notes && <p className="mt-2 font-sans text-xs text-[#0c0f3d]/70">{t.notes}</p>}
                        </div>
                        <button onClick={() => remove(t.id)} data-testid={`task-delete-${t.id}`} className="text-[#0c0f3d]/40 hover:text-[#e47a6a]">
                            <Trash2 size={14} />
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
}

function TaskForm({ onClose, onSaved }) {
    const [title, setTitle] = useState("");
    const [email, setEmail] = useState("");
    const [due, setDue] = useState("");
    const [notes, setNotes] = useState("");

    const save = async (e) => {
        e.preventDefault();
        if (!title.trim()) { toast.error("Title required"); return; }
        await axios.post(`${API}/admin/tasks`, {
            title,
            contact_email: email || null,
            due_date: due ? `${due}T09:00:00` : null,
            notes,
        }, { withCredentials: true });
        toast.success("Task added");
        onSaved();
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-6" onClick={onClose} data-testid="task-form-modal">
            <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-[500px] p-8">
                <h3 className="font-serif text-2xl text-[#0c0f3d] mb-6">New task</h3>
                <div className="space-y-4">
                    <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" data-testid="task-form-title" className="w-full px-3 py-2.5 border border-[#e3c7b9] text-sm outline-none focus:border-[#0c0f3d]" />
                    <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Link to contact email (optional)" data-testid="task-form-email" className="w-full px-3 py-2.5 border border-[#e3c7b9] text-sm outline-none focus:border-[#0c0f3d]" />
                    <input type="date" value={due} onChange={(e) => setDue(e.target.value)} data-testid="task-form-due" className="w-full px-3 py-2.5 border border-[#e3c7b9] text-sm outline-none focus:border-[#0c0f3d]" />
                    <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes" rows={3} data-testid="task-form-notes" className="w-full px-3 py-2.5 border border-[#e3c7b9] text-sm outline-none focus:border-[#0c0f3d] resize-none" />
                </div>
                <div className="flex gap-3 mt-6">
                    <button type="submit" data-testid="task-form-save" className="flex-1 px-5 py-3 bg-[#0c0f3d] text-white text-xs uppercase tracking-[0.2em] hover:bg-[#e47a6a]">Save</button>
                    <button type="button" onClick={onClose} data-testid="task-form-cancel" className="px-5 py-3 border border-[#0c0f3d] text-[#0c0f3d] text-xs uppercase tracking-[0.2em]">Cancel</button>
                </div>
            </form>
        </div>
    );
}

// ---------- Bookings ----------
function BookingsPanel() {
    const [rows, setRows] = useState([]);
    const [filter, setFilter] = useState("upcoming");

    const load = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/admin/bookings`, { withCredentials: true });
            setRows(data);
        } catch {
            toast.error("Could not load bookings.");
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const cancel = async (id) => {
        if (!window.confirm("Cancel this booking?")) return;
        await axios.put(`${API}/admin/bookings/${id}`, { status: "cancelled" }, { withCredentials: true });
        toast.success("Cancelled");
        load();
    };

    const now = new Date().toISOString();
    const filtered = rows.filter((b) => {
        if (filter === "upcoming") return b.start_iso >= now && b.status !== "cancelled";
        if (filter === "past") return b.start_iso < now;
        if (filter === "cancelled") return b.status === "cancelled";
        return true;
    });

    return (
        <div data-testid="admin-bookings-panel">
            <PanelHeader eyebrow="CRM" title="Bookings" />
            <div className="flex gap-2 mt-8">
                {[["upcoming", "Upcoming"], ["past", "Past"], ["cancelled", "Cancelled"], ["all", "All"]].map(([id, label]) => (
                    <button
                        key={id}
                        onClick={() => setFilter(id)}
                        data-testid={`bookings-filter-${id}`}
                        className={`px-4 py-2 text-xs uppercase tracking-[0.2em] border ${
                            filter === id ? "bg-[#0c0f3d] text-white border-[#0c0f3d]" : "text-[#0c0f3d] border-[#0c0f3d]/30 hover:border-[#0c0f3d]"
                        }`}
                    >
                        {label}
                    </button>
                ))}
            </div>

            <div className="mt-6 space-y-3" data-testid="bookings-list">
                {filtered.length === 0 ? (
                    <p className="text-center py-12 font-serif italic text-[#0c0f3d]/60">No bookings in this view.</p>
                ) : filtered.map((b) => {
                    const dt = new Date(b.start_iso);
                    const dateStr = dt.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
                    const timeStr = dt.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: "America/New_York" });
                    return (
                        <div key={b.id} className="bg-white border border-[#e3c7b9] p-5" data-testid={`booking-row-${b.id}`}>
                            <div className="flex items-start justify-between gap-6">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-3 flex-wrap">
                                        <p className="font-serif text-xl text-[#0c0f3d]">{b.consultation_name}</p>
                                        <BookingStatusPill status={b.status} />
                                    </div>
                                    <p className="font-sans text-sm text-[#0c0f3d]/70 mt-1">
                                        {b.client_name} · {b.client_email}{b.client_organization ? ` · ${b.client_organization}` : ""}
                                    </p>
                                    <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-[#0c0f3d]/70">
                                        <span className="inline-flex items-center gap-1.5"><CalendarClock size={12} /> {dateStr}</span>
                                        <span className="inline-flex items-center gap-1.5"><Clock size={12} /> {timeStr} ET · {b.consultation_duration} min</span>
                                        <span className="inline-flex items-center gap-1.5">${b.consultation_price?.toFixed(2) || "0.00"}</span>
                                        {b.zoom_link && (
                                            <a href={b.zoom_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#e47a6a] hover:underline">
                                                <Video size={12} /> Join Zoom
                                            </a>
                                        )}
                                    </div>
                                    {b.notes && <p className="mt-3 font-sans text-xs text-[#0c0f3d]/70 leading-[1.6] bg-[#fbf9f8] p-3 border-l-2 border-[#e47a6a]">{b.notes}</p>}
                                </div>
                                {b.status !== "cancelled" && b.start_iso >= now && (
                                    <button
                                        onClick={() => cancel(b.id)}
                                        data-testid={`booking-cancel-${b.id}`}
                                        className="text-xs uppercase tracking-[0.2em] text-[#0c0f3d]/50 hover:text-[#e47a6a]"
                                    >
                                        Cancel
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// ---------- Invoices ----------
function InvoicesPanel() {
    const [rows, setRows] = useState([]);
    const [filter, setFilter] = useState("all");
    const [showForm, setShowForm] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [markPaid, setMarkPaid] = useState(null);  // invoice being marked

    const load = useCallback(async () => {
        setRefreshing(true);
        try {
            const { data } = await axios.get(`${API}/admin/invoices`, { withCredentials: true });
            setRows(data);
        } catch {
            toast.error("Could not load invoices.");
        }
        setRefreshing(false);
    }, []);
    useEffect(() => { load(); }, [load]);

    const filtered = rows.filter((i) => {
        if (filter === "all") return true;
        return i.status === filter;
    });

    const resend = async (i) => {
        try {
            await axios.post(`${API}/admin/invoices/${i.id}/resend`, {}, { withCredentials: true });
            toast.success("Invoice resent to client.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Resend failed.");
        }
    };
    const voidInv = async (i) => {
        if (!window.confirm(`Void invoice ${i.invoice_number}? This cannot be undone.`)) return;
        try {
            await axios.post(`${API}/admin/invoices/${i.id}/void`, {}, { withCredentials: true });
            toast.success("Invoice voided.");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Void failed.");
        }
    };

    return (
        <div data-testid="admin-invoices-panel">
            <PanelHeader eyebrow="CRM" title="Invoices" />
            <div className="flex items-center justify-between mt-8 flex-wrap gap-3">
                <div className="flex gap-2 flex-wrap">
                    {[["all","All"],["open","Sent"],["paid","Paid"],["draft","Draft"],["void","Void"],["uncollectible","Overdue"]].map(([id,label]) => (
                        <button
                            key={id}
                            onClick={() => setFilter(id)}
                            data-testid={`invoices-filter-${id}`}
                            className={`px-4 py-2 text-xs uppercase tracking-[0.2em] border ${
                                filter === id ? "bg-[#0c0f3d] text-white border-[#0c0f3d]" : "text-[#0c0f3d] border-[#0c0f3d]/30 hover:border-[#0c0f3d]"
                            }`}
                        >
                            {label}
                        </button>
                    ))}
                </div>
                <div className="flex gap-3">
                    <button
                        onClick={load}
                        disabled={refreshing}
                        data-testid="invoices-refresh-btn"
                        className="inline-flex items-center gap-2 px-4 py-2.5 border border-[#0c0f3d] text-[#0c0f3d] text-xs uppercase tracking-[0.2em] hover:bg-[#0c0f3d] hover:text-white disabled:opacity-50"
                    >
                        <RefreshCw size={12} className={refreshing ? "animate-spin" : ""} />
                        Sync
                    </button>
                    <button
                        onClick={() => setShowForm(true)}
                        data-testid="invoices-new-btn"
                        className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0c0f3d] text-white text-xs uppercase tracking-[0.2em] hover:bg-[#e47a6a]"
                    >
                        <Plus size={14} /> New invoice
                    </button>
                </div>
            </div>

            {showForm && <InvoiceForm onClose={() => setShowForm(false)} onSent={() => { setShowForm(false); load(); }} />}
            {markPaid && <MarkPaidModal invoice={markPaid} onClose={() => setMarkPaid(null)} onDone={() => { setMarkPaid(null); load(); }} />}

            <div className="mt-6 bg-white border border-[#e3c7b9]" data-testid="invoices-list">
                <table className="w-full">
                    <thead className="bg-[#e3c7b9]/40">
                        <tr>
                            <Th>Invoice #</Th>
                            <Th>Client</Th>
                            <Th>Amount</Th>
                            <Th>Status</Th>
                            <Th>Sent</Th>
                            <Th>Actions</Th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length === 0 ? (
                            <tr><td colSpan={6} className="text-center py-12 font-serif italic text-[#0c0f3d]/60">No invoices in this view.</td></tr>
                        ) : filtered.map((i) => (
                            <tr key={i.id} className="border-t border-[#e3c7b9]" data-testid={`invoice-row-${i.id}`}>
                                <Td>
                                    <p className="font-sans text-sm text-[#0c0f3d] font-medium">{i.invoice_number || "—"}</p>
                                </Td>
                                <Td>
                                    <p className="font-sans text-sm text-[#0c0f3d]">{i.client_name || i.client_email}</p>
                                    {i.client_organization && <p className="font-sans text-xs text-[#0c0f3d]/60">{i.client_organization}</p>}
                                </Td>
                                <Td>
                                    <p className="font-serif text-lg text-[#0c0f3d]">{fmtMoney(i.total_amount)}</p>
                                    {i.discount_amount > 0 && (
                                        <p className="font-sans text-[10px] uppercase tracking-[0.2em] text-[#e47a6a] mt-0.5">−{fmtMoney(i.discount_amount)} {i.discount_type === "percent" ? `(${i.discount_value}%)` : "off"}</p>
                                    )}
                                    {i.status === "paid" && <p className="font-sans text-[10px] uppercase tracking-[0.2em] text-[#4a7c59]">paid {fmtMoney(i.amount_paid || 0)}{i.offline_payment_method ? ` · ${i.offline_payment_method}` : ""}</p>}
                                </Td>
                                <Td><InvoiceStatusPill status={i.status} /></Td>
                                <Td>
                                    <span className="font-sans text-xs text-[#0c0f3d]/60">{i.sent_at ? new Date(i.sent_at).toLocaleDateString() : "—"}</span>
                                </Td>
                                <Td>
                                    <div className="flex items-center gap-2">
                                        {i.hosted_invoice_url && (
                                            <a
                                                href={i.hosted_invoice_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                data-testid={`invoice-view-${i.id}`}
                                                title="View hosted invoice"
                                                className="text-[#0c0f3d]/60 hover:text-[#e47a6a]"
                                            >
                                                <ExternalLink size={14} />
                                            </a>
                                        )}
                                        {i.status !== "paid" && i.status !== "void" && (
                                            <>
                                                <button
                                                    onClick={() => setMarkPaid(i)}
                                                    data-testid={`invoice-markpaid-${i.id}`}
                                                    title="Mark as paid (offline)"
                                                    className="text-[#0c0f3d]/60 hover:text-[#4a7c59]"
                                                >
                                                    <DollarSign size={14} />
                                                </button>
                                                <button
                                                    onClick={() => resend(i)}
                                                    data-testid={`invoice-resend-${i.id}`}
                                                    title="Resend email"
                                                    className="text-[#0c0f3d]/60 hover:text-[#e47a6a]"
                                                >
                                                    <Mail size={14} />
                                                </button>
                                                <button
                                                    onClick={() => voidInv(i)}
                                                    data-testid={`invoice-void-${i.id}`}
                                                    title="Void invoice"
                                                    className="text-[#0c0f3d]/60 hover:text-[#e47a6a]"
                                                >
                                                    <Ban size={14} />
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </Td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function InvoiceStatusPill({ status }) {
    const map = {
        draft: { label: "Draft", cls: "bg-black/10 text-black/60" },
        open: { label: "Sent", cls: "bg-[#30b8cf]/20 text-[#30b8cf]" },
        paid: { label: "Paid", cls: "bg-[#4a7c59]/20 text-[#4a7c59]" },
        uncollectible: { label: "Overdue", cls: "bg-[#e47a6a]/20 text-[#e47a6a]" },
        void: { label: "Void", cls: "bg-black/10 text-black/40" },
    };
    const m = map[status] || { label: status, cls: "bg-gray-100" };
    return <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 ${m.cls}`}>{m.label}</span>;
}

function MarkPaidModal({ invoice, onClose, onDone }) {
    const [method, setMethod] = useState("zelle");
    const [note, setNote] = useState("");
    const [saving, setSaving] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await axios.post(`${API}/admin/invoices/${invoice.id}/mark-paid`, { payment_method: method, note: note || null }, { withCredentials: true });
            toast.success(`Marked as paid via ${method}.`);
            onDone();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not mark as paid.");
        }
        setSaving(false);
    };
    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-6" onClick={onClose} data-testid="markpaid-modal">
            <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-[480px] p-8">
                <div className="flex items-start justify-between mb-6">
                    <div>
                        <p className="font-sans text-[10px] uppercase tracking-[0.3em] text-[#e47a6a]">Mark as paid</p>
                        <h3 className="font-serif text-2xl text-[#0c0f3d] mt-1">{invoice.invoice_number}</h3>
                        <p className="font-sans text-sm text-[#0c0f3d]/60 mt-1">{fmtMoney(invoice.total_amount)} · {invoice.client_email}</p>
                    </div>
                    <button type="button" onClick={onClose} data-testid="markpaid-close" className="p-1 hover:bg-[#e3c7b9]/40"><X size={20} /></button>
                </div>
                <p className="font-serif text-sm text-[#0c0f3d]/70 mb-5 leading-[1.6]">Use this for payments received outside Stripe (Zelle, wire, check, cash). Stripe will be updated as <em>paid out-of-band</em> so it stops sending reminders. Zero processing fees.</p>
                <div className="space-y-4">
                    <div>
                        <label className="font-sans text-[10px] uppercase tracking-[0.25em] text-[#6d91ad] block mb-2">Payment method</label>
                        <select value={method} onChange={(e) => setMethod(e.target.value)} data-testid="markpaid-method" className="w-full px-3 py-2.5 border border-[#e3c7b9] text-sm outline-none focus:border-[#0c0f3d] bg-white">
                            <option value="zelle">Zelle</option>
                            <option value="check">Check</option>
                            <option value="wire">Wire transfer</option>
                            <option value="cash">Cash</option>
                            <option value="ach_manual">ACH (manual, not Stripe)</option>
                            <option value="other">Other</option>
                        </select>
                    </div>
                    <div>
                        <label className="font-sans text-[10px] uppercase tracking-[0.25em] text-[#6d91ad] block mb-2">Reference / note (optional)</label>
                        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Zelle ref #A1B2C3, deposited Nov 4" data-testid="markpaid-note" className="w-full px-3 py-2.5 border border-[#e3c7b9] text-sm outline-none focus:border-[#0c0f3d]" />
                    </div>
                </div>
                <div className="flex gap-3 mt-6 pt-6 border-t border-[#e3c7b9]">
                    <button type="button" onClick={onClose} className="px-5 py-2.5 border border-[#0c0f3d] text-[#0c0f3d] text-xs uppercase tracking-[0.2em] hover:bg-[#0c0f3d] hover:text-white">Cancel</button>
                    <button type="submit" disabled={saving} data-testid="markpaid-submit" className="flex-1 px-5 py-2.5 bg-[#0c0f3d] text-white text-xs uppercase tracking-[0.2em] hover:bg-[#4a7c59] disabled:opacity-50">
                        {saving ? "Saving…" : "Mark as paid"}
                    </button>
                </div>
            </form>
        </div>
    );
}

function InvoiceForm({ onClose, onSent, prefillEmail = "" }) {
    const [contacts, setContacts] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [f, setF] = useState({
        client_email: prefillEmail,
        client_name: "",
        client_organization: "",
        line_items: [{ description: "", amount: "", quantity: 1 }],
        memo: "",
        due_days: 7,
        footer: "",
        accept_ach: true,
        discount_type: "none",  // 'none' | 'percent' | 'amount'
        discount_value: "",
        discount_label: "",
    });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        axios.get(`${API}/admin/contacts`, { withCredentials: true })
            .then((r) => setContacts(r.data.contacts || []))
            .catch(() => {});
    }, []);

    const emailSuggestions = f.client_email.length >= 2
        ? contacts.filter((c) => c.email.includes(f.client_email.toLowerCase()) || (c.name || "").toLowerCase().includes(f.client_email.toLowerCase())).slice(0, 6)
        : [];

    const pickContact = (c) => {
        setF({ ...f, client_email: c.email, client_name: c.name || "", client_organization: c.organization || "" });
        setShowSuggestions(false);
    };

    const subtotal = f.line_items.reduce((s, li) => s + (parseFloat(li.amount) || 0) * (parseInt(li.quantity) || 1), 0);
    const discountValueNum = parseFloat(f.discount_value) || 0;
    const discountAmount = f.discount_type === "percent"
        ? subtotal * (Math.min(100, Math.max(0, discountValueNum)) / 100)
        : f.discount_type === "amount"
            ? Math.min(subtotal, discountValueNum)
            : 0;
    const netTotal = Math.max(0, subtotal - discountAmount);

    const setItem = (idx, patch) => {
        const next = [...f.line_items];
        next[idx] = { ...next[idx], ...patch };
        setF({ ...f, line_items: next });
    };
    const addItem = () => setF({ ...f, line_items: [...f.line_items, { description: "", amount: "", quantity: 1 }] });
    const removeItem = (idx) => setF({ ...f, line_items: f.line_items.filter((_, i) => i !== idx) });

    const submit = async (e) => {
        e.preventDefault();
        if (!f.client_email) { toast.error("Client email required"); return; }
        const items = f.line_items
            .filter((li) => li.description.trim() && parseFloat(li.amount) > 0)
            .map((li) => ({ description: li.description.trim(), amount: parseFloat(li.amount), quantity: parseInt(li.quantity) || 1 }));
        if (!items.length) { toast.error("At least one line item required"); return; }
        setSaving(true);
        try {
            const payload = {
                client_email: f.client_email,
                client_name: f.client_name || null,
                client_organization: f.client_organization || null,
                line_items: items,
                memo: f.memo || null,
                footer: f.footer || null,
                due_days: parseInt(f.due_days) || 7,
                accept_ach: !!f.accept_ach,
            };
            if (f.discount_type !== "none" && discountValueNum > 0) {
                payload.discount_type = f.discount_type;
                payload.discount_value = discountValueNum;
                payload.discount_label = f.discount_label || null;
            }
            await axios.post(`${API}/admin/invoices`, payload, { withCredentials: true });
            toast.success("Invoice sent to client via Stripe.");
            onSent();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not send invoice.");
        }
        setSaving(false);
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-6 py-8 overflow-y-auto" onClick={onClose} data-testid="invoice-form-modal">
            <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-[720px] p-8 my-auto">
                <div className="flex items-start justify-between mb-6">
                    <div>
                        <p className="font-sans text-[10px] uppercase tracking-[0.3em] text-[#e47a6a]">New Invoice</p>
                        <h3 className="font-serif text-3xl text-[#0c0f3d] mt-1">Send an invoice</h3>
                        <p className="font-sans text-xs text-[#0c0f3d]/60 mt-1">Stripe will email a hosted payment page to your client.</p>
                    </div>
                    <button type="button" onClick={onClose} data-testid="invoice-form-close" className="p-1 hover:bg-[#e3c7b9]/40"><X size={20} /></button>
                </div>

                <div className="space-y-6">
                    {/* Client */}
                    <div>
                        <p className="font-sans text-[10px] uppercase tracking-[0.25em] text-[#6d91ad] mb-3">Client</p>
                        <div className="relative">
                            <input
                                required
                                type="email"
                                placeholder="client@example.com"
                                value={f.client_email}
                                onChange={(e) => { setF({ ...f, client_email: e.target.value }); setShowSuggestions(true); }}
                                onFocus={() => setShowSuggestions(true)}
                                data-testid="invoice-form-email"
                                className="w-full px-3 py-2.5 border border-[#e3c7b9] text-sm outline-none focus:border-[#0c0f3d]"
                            />
                            {showSuggestions && emailSuggestions.length > 0 && (
                                <div className="absolute z-10 top-full left-0 right-0 bg-white border border-[#e3c7b9] mt-1 max-h-64 overflow-y-auto shadow-lg">
                                    {emailSuggestions.map((c) => (
                                        <button
                                            key={c.email}
                                            type="button"
                                            onClick={() => pickContact(c)}
                                            data-testid={`invoice-form-suggest-${c.email}`}
                                            className="w-full text-left px-3 py-2 hover:bg-[#e3c7b9]/30 border-b border-[#e3c7b9] last:border-0"
                                        >
                                            <p className="font-sans text-sm text-[#0c0f3d]">{c.name || c.email}</p>
                                            <p className="font-sans text-xs text-[#0c0f3d]/60">{c.email} · {c.organization || "—"}</p>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="grid grid-cols-2 gap-3 mt-3">
                            <input
                                placeholder="Client name"
                                value={f.client_name}
                                onChange={(e) => setF({ ...f, client_name: e.target.value })}
                                data-testid="invoice-form-name"
                                className="w-full px-3 py-2.5 border border-[#e3c7b9] text-sm outline-none focus:border-[#0c0f3d]"
                            />
                            <input
                                placeholder="Organization"
                                value={f.client_organization}
                                onChange={(e) => setF({ ...f, client_organization: e.target.value })}
                                data-testid="invoice-form-org"
                                className="w-full px-3 py-2.5 border border-[#e3c7b9] text-sm outline-none focus:border-[#0c0f3d]"
                            />
                        </div>
                    </div>

                    {/* Line items */}
                    <div>
                        <p className="font-sans text-[10px] uppercase tracking-[0.25em] text-[#6d91ad] mb-3">Line items</p>
                        <div className="space-y-2">
                            {f.line_items.map((li, idx) => (
                                <div key={idx} className="grid grid-cols-[1fr_100px_80px_32px] gap-2 items-start" data-testid={`invoice-line-${idx}`}>
                                    <input
                                        placeholder="Description (e.g. Educational Strategy Consultation)"
                                        value={li.description}
                                        onChange={(e) => setItem(idx, { description: e.target.value })}
                                        data-testid={`invoice-line-desc-${idx}`}
                                        className="w-full px-3 py-2.5 border border-[#e3c7b9] text-sm outline-none focus:border-[#0c0f3d]"
                                    />
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        placeholder="0.00"
                                        value={li.amount}
                                        onChange={(e) => setItem(idx, { amount: e.target.value })}
                                        data-testid={`invoice-line-amount-${idx}`}
                                        className="w-full px-3 py-2.5 border border-[#e3c7b9] text-sm outline-none focus:border-[#0c0f3d] text-right"
                                    />
                                    <input
                                        type="number"
                                        min="1"
                                        step="1"
                                        placeholder="1"
                                        value={li.quantity}
                                        onChange={(e) => setItem(idx, { quantity: e.target.value })}
                                        data-testid={`invoice-line-qty-${idx}`}
                                        className="w-full px-3 py-2.5 border border-[#e3c7b9] text-sm outline-none focus:border-[#0c0f3d] text-center"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => removeItem(idx)}
                                        disabled={f.line_items.length === 1}
                                        data-testid={`invoice-line-remove-${idx}`}
                                        className="p-2 text-[#0c0f3d]/50 hover:text-[#e47a6a] disabled:opacity-30 disabled:cursor-not-allowed"
                                    >
                                        <X size={14} />
                                    </button>
                                </div>
                            ))}
                        </div>
                        <button
                            type="button"
                            onClick={addItem}
                            data-testid="invoice-line-add"
                            className="mt-3 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#0c0f3d] hover:text-[#e47a6a]"
                        >
                            <Plus size={12} /> Add line
                        </button>
                        <div className="mt-4 flex items-baseline justify-between border-t border-[#e3c7b9] pt-4">
                            <span className="font-sans text-xs uppercase tracking-[0.25em] text-[#6d91ad]">Subtotal</span>
                            <span className="font-serif text-2xl text-[#0c0f3d]" data-testid="invoice-form-subtotal">{fmtMoney(subtotal)}</span>
                        </div>
                    </div>

                    {/* Memo, due, footer */}
                    <div className="grid grid-cols-[1fr_140px] gap-3">
                        <div>
                            <label className="font-sans text-[10px] uppercase tracking-[0.25em] text-[#6d91ad] block mb-2">Memo (optional, shown on invoice)</label>
                            <textarea
                                rows={2}
                                value={f.memo}
                                onChange={(e) => setF({ ...f, memo: e.target.value })}
                                data-testid="invoice-form-memo"
                                className="w-full px-3 py-2.5 border border-[#e3c7b9] text-sm outline-none focus:border-[#0c0f3d] resize-none"
                                placeholder="Description of what this invoice covers…"
                            />
                        </div>
                        <div>
                            <label className="font-sans text-[10px] uppercase tracking-[0.25em] text-[#6d91ad] block mb-2">Due (days)</label>
                            <input
                                type="number"
                                min="0"
                                value={f.due_days}
                                onChange={(e) => setF({ ...f, due_days: e.target.value })}
                                data-testid="invoice-form-due-days"
                                className="w-full px-3 py-2.5 border border-[#e3c7b9] text-sm outline-none focus:border-[#0c0f3d]"
                            />
                        </div>
                    </div>

                    {/* Discount + payment methods */}
                    <div className="bg-[#fbf9f8] border border-[#e3c7b9] p-4 space-y-4">
                        <div>
                            <p className="font-sans text-[10px] uppercase tracking-[0.25em] text-[#e47a6a] mb-3">Discount (optional)</p>
                            <div className="flex flex-wrap items-center gap-2">
                                <select
                                    value={f.discount_type}
                                    onChange={(e) => setF({ ...f, discount_type: e.target.value })}
                                    data-testid="invoice-form-discount-type"
                                    className="px-3 py-2 border border-[#e3c7b9] text-sm outline-none focus:border-[#0c0f3d] bg-white"
                                >
                                    <option value="none">No discount</option>
                                    <option value="percent">% off</option>
                                    <option value="amount">$ off</option>
                                </select>
                                {f.discount_type !== "none" && (
                                    <>
                                        <input
                                            type="number"
                                            min="0"
                                            step={f.discount_type === "percent" ? "1" : "0.01"}
                                            max={f.discount_type === "percent" ? "99" : undefined}
                                            value={f.discount_value}
                                            onChange={(e) => setF({ ...f, discount_value: e.target.value })}
                                            data-testid="invoice-form-discount-value"
                                            placeholder={f.discount_type === "percent" ? "10" : "100.00"}
                                            className="w-24 px-3 py-2 border border-[#e3c7b9] text-sm outline-none focus:border-[#0c0f3d]"
                                        />
                                        <span className="font-sans text-sm text-[#0c0f3d]/60">{f.discount_type === "percent" ? "%" : "$"}</span>
                                        <input
                                            value={f.discount_label}
                                            onChange={(e) => setF({ ...f, discount_label: e.target.value })}
                                            placeholder="Label (e.g. Launch client courtesy)"
                                            data-testid="invoice-form-discount-label"
                                            className="flex-1 min-w-[180px] px-3 py-2 border border-[#e3c7b9] text-sm outline-none focus:border-[#0c0f3d]"
                                        />
                                    </>
                                )}
                            </div>
                            {discountAmount > 0 && (
                                <div className="mt-3 pt-3 border-t border-[#e3c7b9] flex justify-between items-center">
                                    <p className="font-sans text-xs text-[#0c0f3d]/70">
                                        <span className="font-sans text-[#0c0f3d]">Subtotal {fmtMoney(subtotal)}</span>
                                        <span className="text-[#e47a6a] ml-3">− Discount {fmtMoney(discountAmount)}</span>
                                    </p>
                                    <p className="font-serif text-lg text-[#0c0f3d]">= {fmtMoney(netTotal)}</p>
                                </div>
                            )}
                        </div>
                        <label className="flex items-start gap-3 cursor-pointer pt-3 border-t border-[#e3c7b9]">
                            <input
                                type="checkbox"
                                checked={f.accept_ach}
                                onChange={(e) => setF({ ...f, accept_ach: e.target.checked })}
                                data-testid="invoice-form-accept-ach"
                                className="mt-1 accent-[#0c0f3d]"
                            />
                            <span className="font-sans text-sm text-[#0c0f3d] leading-[1.5]">
                                Accept bank transfer (ACH)
                                <span className="block text-xs text-[#0c0f3d]/60 mt-0.5">0.8% Stripe fee, capped at $5 per invoice — vs 2.9%+ on cards. Requires ACH enabled in your Stripe dashboard.</span>
                            </span>
                        </label>
                    </div>
                </div>

                <div className="flex gap-3 mt-8">
                    <button
                        type="submit"
                        disabled={saving}
                        data-testid="invoice-form-send"
                        className="flex-1 px-6 py-3.5 bg-[#0c0f3d] text-white text-xs uppercase tracking-[0.25em] hover:bg-[#e47a6a] disabled:opacity-50"
                    >
                        {saving ? "Sending…" : `Send Invoice for ${fmtMoney(netTotal)}`}
                    </button>
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="invoice-form-cancel"
                        className="px-6 py-3.5 border border-[#0c0f3d] text-[#0c0f3d] text-xs uppercase tracking-[0.25em]"
                    >
                        Cancel
                    </button>
                </div>
            </form>
        </div>
    );
}

// ---------- Services ----------
function ServicesPanel() {
    const [rows, setRows] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);

    const load = useCallback(async () => {
        const { data } = await axios.get(`${API}/admin/services`, { withCredentials: true });
        setRows(data);
    }, []);
    useEffect(() => { load(); }, [load]);

    const remove = async (id) => {
        if (!window.confirm("Delete this service?")) return;
        await axios.delete(`${API}/admin/services/${id}`, { withCredentials: true });
        load();
    };

    return (
        <div data-testid="admin-services-panel">
            <PanelHeader eyebrow="CRM" title="Services & Offerings" />
            <div className="flex justify-end mt-8">
                <button
                    onClick={() => { setEditing(null); setShowForm(true); }}
                    data-testid="services-new-btn"
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0c0f3d] text-white text-xs uppercase tracking-[0.2em] hover:bg-[#e47a6a]"
                >
                    <Plus size={14} /> New service
                </button>
            </div>

            {(showForm || editing) && (
                <ServiceForm
                    initial={editing}
                    onClose={() => { setShowForm(false); setEditing(null); }}
                    onSaved={() => { setShowForm(false); setEditing(null); load(); }}
                />
            )}

            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="services-list">
                {rows.length === 0 ? (
                    <p className="col-span-2 text-center py-12 font-serif italic text-[#0c0f3d]/60">
                        No services yet. Add your 9-Module curriculum, Keynote, Faculty Training, etc.
                    </p>
                ) : rows.map((s) => (
                    <div key={s.id} className="bg-white border border-[#e3c7b9] p-6" data-testid={`service-row-${s.id}`}>
                        <div className="flex items-start justify-between">
                            <div>
                                <p className="font-sans text-[10px] uppercase tracking-[0.25em] text-[#6d91ad]">{s.category || "Service"}</p>
                                <h3 className="font-serif text-xl text-[#0c0f3d] mt-1">{s.name}</h3>
                            </div>
                            <p className="font-serif text-2xl text-[#0c0f3d]">${s.price.toFixed(0)}</p>
                        </div>
                        {s.description && <p className="font-sans text-sm text-[#0c0f3d]/70 mt-3 leading-[1.6]">{s.description}</p>}
                        <div className="mt-4 flex gap-3">
                            <button onClick={() => setEditing(s)} data-testid={`service-edit-${s.id}`} className="text-xs uppercase tracking-[0.2em] text-[#0c0f3d] hover:text-[#e47a6a]">Edit</button>
                            <button onClick={() => remove(s.id)} data-testid={`service-delete-${s.id}`} className="text-xs uppercase tracking-[0.2em] text-[#0c0f3d]/60 hover:text-[#e47a6a]">Delete</button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function ServiceForm({ initial, onClose, onSaved }) {
    const [f, setF] = useState({
        name: initial?.name || "",
        description: initial?.description || "",
        price: initial?.price || 0,
        duration: initial?.duration || "",
        category: initial?.category || "",
    });
    const save = async (e) => {
        e.preventDefault();
        const payload = { ...f, price: parseFloat(f.price) || 0 };
        if (initial) {
            await axios.put(`${API}/admin/services/${initial.id}`, payload, { withCredentials: true });
        } else {
            await axios.post(`${API}/admin/services`, payload, { withCredentials: true });
        }
        toast.success("Saved");
        onSaved();
    };
    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-6" onClick={onClose} data-testid="service-form-modal">
            <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-[560px] p-8 max-h-[90vh] overflow-y-auto">
                <h3 className="font-serif text-2xl text-[#0c0f3d] mb-6">{initial ? "Edit" : "New"} service</h3>
                <div className="space-y-4">
                    <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Service name" required data-testid="service-form-name" className="w-full px-3 py-2.5 border border-[#e3c7b9] text-sm" />
                    <input value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} placeholder="Category (e.g. Keynote, Workshop)" data-testid="service-form-category" className="w-full px-3 py-2.5 border border-[#e3c7b9] text-sm" />
                    <input value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} type="number" step="0.01" placeholder="Price" data-testid="service-form-price" className="w-full px-3 py-2.5 border border-[#e3c7b9] text-sm" />
                    <input value={f.duration} onChange={(e) => setF({ ...f, duration: e.target.value })} placeholder="Duration (e.g. 90 min)" data-testid="service-form-duration" className="w-full px-3 py-2.5 border border-[#e3c7b9] text-sm" />
                    <textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="Description" rows={4} data-testid="service-form-description" className="w-full px-3 py-2.5 border border-[#e3c7b9] text-sm resize-none" />
                </div>
                <div className="flex gap-3 mt-6">
                    <button type="submit" data-testid="service-form-save" className="flex-1 px-5 py-3 bg-[#0c0f3d] text-white text-xs uppercase tracking-[0.2em] hover:bg-[#e47a6a]">Save</button>
                    <button type="button" onClick={onClose} data-testid="service-form-cancel" className="px-5 py-3 border border-[#0c0f3d] text-[#0c0f3d] text-xs uppercase tracking-[0.2em]">Cancel</button>
                </div>
            </form>
        </div>
    );
}

// ---------- Contract Templates ----------
function ContractTemplatesPanel() {
    const [templates, setTemplates] = useState([]);
    const [tokens, setTokens] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);

    const load = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/admin/contract-templates`, { withCredentials: true });
            setTemplates(data.templates || []);
            setTokens(data.tokens || []);
        } catch (e) {
            toast.error("Could not load templates.");
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const remove = async (t) => {
        if (!window.confirm(`Delete template "${t.name}"?`)) return;
        try {
            await axios.delete(`${API}/admin/contract-templates/${t.id}`, { withCredentials: true });
            toast.success("Template deleted.");
            load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Could not delete."); }
    };

    return (
        <div data-testid="admin-templates-panel">
            <PanelHeader eyebrow="CRM" title="Contract templates" />
            <p className="font-serif text-lg text-[#0c0f3d]/70 mt-4 max-w-[720px]">Paste your lawyer-drafted contracts once. Use tokens like <code className="bg-[#e3c7b9]/40 px-1 py-0.5 text-xs">{"{{client_name}}"}</code> or <code className="bg-[#e3c7b9]/40 px-1 py-0.5 text-xs">{"{{total}}"}</code> to auto-personalize every proposal.</p>

            <div className="flex justify-end mt-6">
                <button
                    onClick={() => { setEditing(null); setShowForm(true); }}
                    data-testid="templates-new-btn"
                    className="px-4 py-2 bg-[#0c0f3d] text-white text-xs uppercase tracking-[0.2em] hover:bg-[#e47a6a] flex items-center gap-2"
                ><Plus size={14} /> New template</button>
            </div>

            {showForm && (
                <ContractTemplateForm
                    initial={editing}
                    tokens={tokens}
                    onClose={() => { setShowForm(false); setEditing(null); }}
                    onSaved={() => { setShowForm(false); setEditing(null); load(); }}
                />
            )}

            <div className="mt-6 bg-white border border-[#e3c7b9]" data-testid="templates-list">
                <table className="w-full">
                    <thead className="bg-[#fbf9f8]">
                        <tr>
                            <Th>Template</Th>
                            <Th>Category</Th>
                            <Th>Last updated</Th>
                            <Th>Actions</Th>
                        </tr>
                    </thead>
                    <tbody>
                        {templates.length === 0 ? (
                            <tr><td colSpan={4} className="text-center py-12 font-serif italic text-[#0c0f3d]/60">No templates yet. Paste one in to get started.</td></tr>
                        ) : templates.map((t) => (
                            <tr key={t.id} className="border-t border-[#e3c7b9]" data-testid={`template-row-${t.id}`}>
                                <Td>
                                    <button onClick={() => { setEditing(t); setShowForm(true); }} data-testid={`template-open-${t.id}`} className="text-left">
                                        <p className="font-serif text-base text-[#0c0f3d] hover:text-[#e47a6a]">{t.name}</p>
                                        {t.notes && <p className="font-sans text-xs text-[#0c0f3d]/60 mt-1">{t.notes}</p>}
                                    </button>
                                </Td>
                                <Td><span className="text-[10px] uppercase tracking-[0.2em] px-2 py-1 bg-[#e3c7b9]/40 text-[#0c0f3d]">{t.category || "General"}</span></Td>
                                <Td><p className="font-sans text-xs text-[#0c0f3d]/70">{new Date(t.updated_at).toLocaleDateString()}</p></Td>
                                <Td>
                                    <div className="flex items-center gap-2">
                                        <button onClick={() => { setEditing(t); setShowForm(true); }} title="Edit" data-testid={`template-edit-${t.id}`} className="p-1.5 text-[#6d91ad] hover:text-[#0c0f3d]"><PenSquare size={14} /></button>
                                        <button onClick={() => remove(t)} title="Delete" data-testid={`template-delete-${t.id}`} className="p-1.5 text-[#0c0f3d]/50 hover:text-[#e47a6a]"><Trash2 size={14} /></button>
                                    </div>
                                </Td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function ContractTemplateForm({ initial = null, tokens = [], onClose, onSaved }) {
    const isEdit = !!initial;
    const [f, setF] = useState(() => initial || { name: "", category: "General", body: "", notes: "" });
    const [saving, setSaving] = useState(false);

    const insertToken = (tok) => {
        const el = document.querySelector('[data-testid="template-form-body"]');
        if (!el) { setF({ ...f, body: (f.body || "") + " " + tok }); return; }
        const start = el.selectionStart || 0;
        const end = el.selectionEnd || 0;
        const before = (f.body || "").slice(0, start);
        const after = (f.body || "").slice(end);
        const next = before + tok + after;
        setF({ ...f, body: next });
        setTimeout(() => { el.focus(); el.selectionStart = el.selectionEnd = start + tok.length; }, 10);
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!f.name.trim() || !f.body.trim()) { toast.error("Name and body required"); return; }
        setSaving(true);
        try {
            const payload = { name: f.name.trim(), category: f.category || "General", body: f.body, notes: f.notes || "" };
            if (isEdit) {
                await axios.put(`${API}/admin/contract-templates/${initial.id}`, payload, { withCredentials: true });
                toast.success("Template updated.");
            } else {
                await axios.post(`${API}/admin/contract-templates`, payload, { withCredentials: true });
                toast.success("Template saved.");
            }
            onSaved();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not save.");
        }
        setSaving(false);
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-6 py-8 overflow-y-auto" onClick={onClose} data-testid="template-form-modal">
            <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-[820px] p-8 my-auto">
                <div className="flex items-start justify-between mb-6">
                    <div>
                        <p className="font-sans text-[10px] uppercase tracking-[0.3em] text-[#e47a6a]">{isEdit ? "Edit template" : "New template"}</p>
                        <h3 className="font-serif text-3xl text-[#0c0f3d] mt-1">Your contract</h3>
                        <p className="font-sans text-xs text-[#0c0f3d]/60 mt-1">Paste your lawyer-drafted contract below. Insert merge tokens where the client&rsquo;s info should appear.</p>
                    </div>
                    <button type="button" onClick={onClose} data-testid="template-form-close" className="p-1 hover:bg-[#e3c7b9]/40"><X size={20} /></button>
                </div>

                <div className="grid grid-cols-2 gap-3 mb-4">
                    <div>
                        <p className="font-sans text-[10px] uppercase tracking-[0.25em] text-[#6d91ad] mb-2">Template name</p>
                        <input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. Educational Consulting Agreement" data-testid="template-form-name" className="w-full px-3 py-2.5 border border-[#e3c7b9] text-sm outline-none focus:border-[#0c0f3d]" />
                    </div>
                    <div>
                        <p className="font-sans text-[10px] uppercase tracking-[0.25em] text-[#6d91ad] mb-2">Category</p>
                        <input value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} placeholder="Educational, Corporate, Nonprofit…" data-testid="template-form-category" className="w-full px-3 py-2.5 border border-[#e3c7b9] text-sm outline-none focus:border-[#0c0f3d]" />
                    </div>
                </div>

                <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                        <p className="font-sans text-[10px] uppercase tracking-[0.25em] text-[#6d91ad]">Contract body</p>
                        <p className="font-sans text-[10px] text-[#0c0f3d]/50">Cursor position determines where the token is inserted</p>
                    </div>
                    <div className="flex flex-wrap gap-1 mb-2">
                        {tokens.map((tok) => (
                            <button
                                key={tok}
                                type="button"
                                onClick={() => insertToken(tok)}
                                data-testid={`template-token-${tok}`}
                                className="text-[10px] font-mono px-2 py-1 border border-[#e3c7b9] text-[#0c0f3d]/70 hover:bg-[#0c0f3d] hover:text-white hover:border-[#0c0f3d]"
                            >{tok}</button>
                        ))}
                    </div>
                    <textarea
                        required
                        value={f.body}
                        onChange={(e) => setF({ ...f, body: e.target.value })}
                        placeholder="Paste your contract here. Use merge tokens above to auto-fill client data on each proposal."
                        data-testid="template-form-body"
                        rows={16}
                        className="w-full px-3 py-2.5 border border-[#e3c7b9] text-sm outline-none focus:border-[#0c0f3d] font-serif leading-[1.6]"
                    />
                </div>

                <div className="mb-4">
                    <p className="font-sans text-[10px] uppercase tracking-[0.25em] text-[#6d91ad] mb-2">Internal notes (optional)</p>
                    <input value={f.notes || ""} onChange={(e) => setF({ ...f, notes: e.target.value })} placeholder="e.g. Reviewed by counsel Feb 2026" data-testid="template-form-notes" className="w-full px-3 py-2.5 border border-[#e3c7b9] text-sm outline-none focus:border-[#0c0f3d]" />
                </div>

                <div className="flex items-center justify-end gap-3 mt-8 pt-6 border-t border-[#e3c7b9]">
                    <button type="button" onClick={onClose} className="px-6 py-2.5 border border-[#0c0f3d] text-[#0c0f3d] text-xs uppercase tracking-[0.2em] hover:bg-[#0c0f3d] hover:text-white">Cancel</button>
                    <button type="submit" disabled={saving} data-testid="template-form-submit" className="px-6 py-2.5 bg-[#0c0f3d] text-white text-xs uppercase tracking-[0.2em] hover:bg-[#e47a6a] disabled:opacity-50">
                        {saving ? "Saving…" : isEdit ? "Update template" : "Save template"}
                    </button>
                </div>
            </form>
        </div>
    );
}


// ---------- Proposals ----------
function ProposalsPanel() {
    const [rows, setRows] = useState([]);
    const [filter, setFilter] = useState("all");
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);
    const [detailId, setDetailId] = useState(null);

    const load = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/admin/proposals`, { withCredentials: true });
            setRows(data || []);
        } catch (e) {
            toast.error("Could not load proposals.");
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const filtered = filter === "all" ? rows : rows.filter((r) => r.status === filter);

    const remove = async (p) => {
        if (!window.confirm(`Delete proposal "${p.title}"? This cannot be undone.`)) return;
        try {
            await axios.delete(`${API}/admin/proposals/${p.id}`, { withCredentials: true });
            toast.success("Proposal deleted.");
            load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Could not delete."); }
    };

    const send = async (p) => {
        try {
            await axios.post(`${API}/admin/proposals/${p.id}/send`, {}, { withCredentials: true });
            toast.success(`Proposal sent to ${p.client_email}.`);
            load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Could not send."); }
    };

    const copyLink = async (url) => {
        try {
            await navigator.clipboard.writeText(url);
            toast.success("Link copied to clipboard.");
        } catch { toast.error("Could not copy — please copy manually."); }
    };

    const generateInvoice = async (p) => {
        if (!window.confirm(`Generate a Stripe invoice from this proposal? ${p.deposit_percentage > 0 ? `The deposit (${fmtMoney(p.deposit_amount)}, ${p.deposit_percentage}%)` : `The full total (${fmtMoney(p.total_amount)})`} will be invoiced.`)) return;
        try {
            const { data } = await axios.post(`${API}/admin/proposals/${p.id}/generate-invoice`, {}, { withCredentials: true });
            toast.success(`Invoice ${data.invoice.invoice_number} created and emailed to client.`);
            load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Could not generate invoice."); }
    };

    return (
        <div data-testid="admin-proposals-panel">
            <PanelHeader eyebrow="CRM" title="Proposals" />
            <div className="flex items-center justify-between mt-8 gap-4 flex-wrap">
                <div className="flex flex-wrap gap-1 bg-white border border-[#e3c7b9] p-1">
                    {["all", "draft", "sent", "viewed", "accepted", "declined", "expired"].map((id) => (
                        <button
                            key={id}
                            onClick={() => setFilter(id)}
                            data-testid={`proposals-filter-${id}`}
                            className={`px-3 py-1.5 text-xs font-sans capitalize transition-colors ${filter === id ? "bg-[#0c0f3d] text-white" : "text-[#0c0f3d]/60 hover:text-[#0c0f3d]"}`}
                        >{id}</button>
                    ))}
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={load}
                        data-testid="proposals-refresh-btn"
                        className="px-3 py-2 border border-[#0c0f3d] text-[#0c0f3d] text-xs uppercase tracking-[0.2em] hover:bg-[#0c0f3d] hover:text-white flex items-center gap-2"
                    ><RefreshCw size={12} /> Refresh</button>
                    <button
                        onClick={() => { setEditing(null); setShowForm(true); }}
                        data-testid="proposals-new-btn"
                        className="px-4 py-2 bg-[#0c0f3d] text-white text-xs uppercase tracking-[0.2em] hover:bg-[#e47a6a] flex items-center gap-2"
                    ><Plus size={14} /> New proposal</button>
                </div>
            </div>

            {showForm && (
                <ProposalForm
                    initial={editing}
                    onClose={() => { setShowForm(false); setEditing(null); }}
                    onSaved={() => { setShowForm(false); setEditing(null); load(); }}
                />
            )}
            {detailId && <ProposalDetailDrawer id={detailId} onClose={() => setDetailId(null)} onChanged={load} onEdit={(p) => { setEditing(p); setShowForm(true); setDetailId(null); }} />}

            <div className="mt-6 bg-white border border-[#e3c7b9]" data-testid="proposals-list">
                <table className="w-full">
                    <thead className="bg-[#fbf9f8]">
                        <tr>
                            <Th>Title</Th>
                            <Th>Client</Th>
                            <Th>Amount</Th>
                            <Th>Status</Th>
                            <Th>Sent</Th>
                            <Th>Actions</Th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length === 0 ? (
                            <tr><td colSpan={6} className="text-center py-12 font-serif italic text-[#0c0f3d]/60">No proposals in this view.</td></tr>
                        ) : filtered.map((p) => (
                            <tr key={p.id} className="border-t border-[#e3c7b9]" data-testid={`proposal-row-${p.id}`}>
                                <Td>
                                    <button onClick={() => setDetailId(p.id)} className="text-left" data-testid={`proposal-open-${p.id}`}>
                                        <p className="font-serif text-base text-[#0c0f3d] hover:text-[#e47a6a]">{p.title}</p>
                                        <p className="font-sans text-xs text-[#0c0f3d]/60">{p.line_items.length} item{p.line_items.length === 1 ? "" : "s"}</p>
                                    </button>
                                </Td>
                                <Td>
                                    <p className="font-sans text-sm text-[#0c0f3d]">{p.client_name || "—"}</p>
                                    <p className="font-sans text-xs text-[#0c0f3d]/60">{p.client_email}</p>
                                </Td>
                                <Td>
                                    <p className="font-serif text-base text-[#0c0f3d]">{fmtMoney(p.total_amount)}</p>
                                    {p.deposit_percentage > 0 && (
                                        <p className="font-sans text-[10px] text-[#e47a6a] tracking-[0.15em] uppercase">Deposit {fmtMoney(p.deposit_amount)} ({p.deposit_percentage}%)</p>
                                    )}
                                </Td>
                                <Td><ProposalStatusPill status={p.status} /></Td>
                                <Td>
                                    <p className="font-sans text-xs text-[#0c0f3d]/70">{p.sent_at ? new Date(p.sent_at).toLocaleDateString() : "—"}</p>
                                </Td>
                                <Td>
                                    <div className="flex items-center gap-2">
                                        {p.public_url && (
                                            <button onClick={() => copyLink(p.public_url)} title="Copy client link" data-testid={`proposal-copy-${p.id}`} className="p-1.5 text-[#6d91ad] hover:text-[#0c0f3d]"><Copy size={14} /></button>
                                        )}
                                        {p.status === "draft" && (
                                            <button onClick={() => send(p)} title="Send to client" data-testid={`proposal-send-${p.id}`} className="p-1.5 text-[#e47a6a] hover:text-[#0c0f3d]"><Send size={14} /></button>
                                        )}
                                        {(p.status === "sent" || p.status === "viewed") && (
                                            <button onClick={() => send(p)} title="Resend email" data-testid={`proposal-resend-${p.id}`} className="p-1.5 text-[#6d91ad] hover:text-[#0c0f3d]"><Mail size={14} /></button>
                                        )}
                                        {p.status === "accepted" && !p.linked_invoice_id && (
                                            <button onClick={() => generateInvoice(p)} title="Generate invoice" data-testid={`proposal-invoice-${p.id}`} className="p-1.5 text-[#4a7c59] hover:text-[#0c0f3d]"><FileText size={14} /></button>
                                        )}
                                        <button onClick={() => remove(p)} title="Delete proposal" data-testid={`proposal-delete-${p.id}`} className="p-1.5 text-[#0c0f3d]/50 hover:text-[#e47a6a]"><Trash2 size={14} /></button>
                                    </div>
                                </Td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function ProposalStatusPill({ status }) {
    const map = {
        draft: { label: "Draft", cls: "bg-black/10 text-black/50" },
        sent: { label: "Sent", cls: "bg-[#6d91ad]/20 text-[#6d91ad]" },
        viewed: { label: "Viewed", cls: "bg-[#30b8cf]/20 text-[#30b8cf]" },
        accepted: { label: "Accepted", cls: "bg-[#4a7c59]/20 text-[#4a7c59]" },
        declined: { label: "Declined", cls: "bg-[#e47a6a]/20 text-[#e47a6a]" },
        expired: { label: "Expired", cls: "bg-[#b59b7c]/20 text-[#b59b7c]" },
    };
    const m = map[status] || { label: status, cls: "bg-gray-100" };
    return <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 ${m.cls}`}>{m.label}</span>;
}

function ProposalForm({ initial = null, onClose, onSaved }) {
    const isEdit = !!initial;
    const defaultTerms = (dp) => (
        dp > 0
            ? `${dp}% deposit due on acceptance. Balance due upon delivery. Cancellation policy: 14 days notice required for a full refund of the deposit.`
            : "Full payment due upon acceptance. Cancellation policy: 14 days notice required for a full refund."
    );
    const [f, setF] = useState(() => initial ? {
        ...initial,
        line_items: initial.line_items?.length ? initial.line_items : [{ description: "", amount: "", quantity: 1 }],
        scope_items: initial.scope_items?.length ? initial.scope_items : [""],
    } : {
        client_email: "",
        client_name: "",
        client_organization: "",
        title: "",
        intro: "",
        scope_items: ["Discovery + strategy call", "Custom curriculum design", "Delivery + facilitation"],
        line_items: [{ description: "", amount: "", quantity: 1 }],
        terms: defaultTerms(50),
        deposit_percentage: 50,
        expires_in_days: 14,
    });
    const [termsTouched, setTermsTouched] = useState(false);
    const [saving, setSaving] = useState(false);
    const [templates, setTemplates] = useState([]);

    useEffect(() => {
        axios.get(`${API}/admin/contract-templates`, { withCredentials: true })
            .then((r) => setTemplates(r.data.templates || []))
            .catch(() => {});
    }, []);

    const applyTemplate = (tplId) => {
        if (!tplId) return;
        const tpl = templates.find((t) => t.id === tplId);
        if (!tpl) return;
        setF((prev) => ({ ...prev, terms: tpl.body }));
        setTermsTouched(true);
        toast.success(`Loaded "${tpl.name}". Tokens will be filled when the proposal is sent.`);
    };

    // Auto-sync terms text with deposit_percentage until admin manually edits terms
    const setDeposit = (val) => {
        const dp = val === "" ? 0 : Math.max(0, Math.min(100, parseInt(val) || 0));
        setF((prev) => ({
            ...prev,
            deposit_percentage: val,
            terms: (!termsTouched && !isEdit) ? defaultTerms(dp) : prev.terms,
        }));
    };

    const total = f.line_items.reduce((s, li) => s + (parseFloat(li.amount) || 0) * (parseInt(li.quantity) || 1), 0);
    const deposit = f.deposit_percentage > 0 ? total * (f.deposit_percentage / 100) : 0;

    const setItem = (idx, patch) => {
        const next = [...f.line_items];
        next[idx] = { ...next[idx], ...patch };
        setF({ ...f, line_items: next });
    };
    const addItem = () => setF({ ...f, line_items: [...f.line_items, { description: "", amount: "", quantity: 1 }] });
    const removeItem = (idx) => setF({ ...f, line_items: f.line_items.filter((_, i) => i !== idx) });

    const setScope = (idx, val) => {
        const next = [...f.scope_items];
        next[idx] = val;
        setF({ ...f, scope_items: next });
    };
    const addScope = () => setF({ ...f, scope_items: [...f.scope_items, ""] });
    const removeScope = (idx) => setF({ ...f, scope_items: f.scope_items.filter((_, i) => i !== idx) });

    const submit = async (e) => {
        e.preventDefault();
        if (!f.client_email) { toast.error("Client email required"); return; }
        if (!f.title) { toast.error("Title required"); return; }
        const items = f.line_items
            .filter((li) => (li.description || "").trim() && parseFloat(li.amount) > 0)
            .map((li) => ({ description: li.description.trim(), amount: parseFloat(li.amount), quantity: parseInt(li.quantity) || 1 }));
        if (!items.length) { toast.error("At least one line item required"); return; }
        setSaving(true);
        try {
            const payload = {
                client_email: f.client_email,
                client_name: f.client_name || null,
                client_organization: f.client_organization || null,
                title: f.title,
                intro: f.intro || null,
                scope_items: f.scope_items.filter((s) => s && s.trim()),
                line_items: items,
                terms: f.terms || null,
                deposit_percentage: parseInt(f.deposit_percentage) || 0,
                expires_in_days: parseInt(f.expires_in_days) || 14,
            };
            if (isEdit) {
                await axios.put(`${API}/admin/proposals/${initial.id}`, payload, { withCredentials: true });
                toast.success("Proposal updated.");
            } else {
                await axios.post(`${API}/admin/proposals`, payload, { withCredentials: true });
                toast.success("Proposal saved as draft. Send when ready.");
            }
            onSaved();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not save proposal.");
        }
        setSaving(false);
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-6 py-8 overflow-y-auto" onClick={onClose} data-testid="proposal-form-modal">
            <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-[820px] p-8 my-auto">
                <div className="flex items-start justify-between mb-6">
                    <div>
                        <p className="font-sans text-[10px] uppercase tracking-[0.3em] text-[#e47a6a]">{isEdit ? "Edit proposal" : "New proposal"}</p>
                        <h3 className="font-serif text-3xl text-[#0c0f3d] mt-1">{isEdit ? "Update the details" : "Craft a proposal"}</h3>
                        <p className="font-sans text-xs text-[#0c0f3d]/60 mt-1">Save as draft. Send when ready — Shana&rsquo;s clients receive a private review + sign link.</p>
                    </div>
                    <button type="button" onClick={onClose} data-testid="proposal-form-close" className="p-1 hover:bg-[#e3c7b9]/40"><X size={20} /></button>
                </div>

                <div className="space-y-6">
                    <div>
                        <p className="font-sans text-[10px] uppercase tracking-[0.25em] text-[#6d91ad] mb-3">Client</p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <input required type="email" placeholder="client@example.com" value={f.client_email} onChange={(e) => setF({ ...f, client_email: e.target.value })} data-testid="proposal-form-email" className="px-3 py-2.5 border border-[#e3c7b9] text-sm outline-none focus:border-[#0c0f3d]" />
                            <input placeholder="Client name" value={f.client_name} onChange={(e) => setF({ ...f, client_name: e.target.value })} data-testid="proposal-form-name" className="px-3 py-2.5 border border-[#e3c7b9] text-sm outline-none focus:border-[#0c0f3d]" />
                            <input placeholder="Organization" value={f.client_organization} onChange={(e) => setF({ ...f, client_organization: e.target.value })} data-testid="proposal-form-org" className="px-3 py-2.5 border border-[#e3c7b9] text-sm outline-none focus:border-[#0c0f3d]" />
                        </div>
                    </div>

                    <div>
                        <p className="font-sans text-[10px] uppercase tracking-[0.25em] text-[#6d91ad] mb-3">Proposal</p>
                        <input required placeholder="Proposal title (e.g. Educational Strategy Engagement)" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="proposal-form-title" className="w-full px-3 py-2.5 border border-[#e3c7b9] text-sm outline-none focus:border-[#0c0f3d]" />
                        <textarea placeholder="Introduction / letter body (optional)" value={f.intro} onChange={(e) => setF({ ...f, intro: e.target.value })} data-testid="proposal-form-intro" rows={3} className="w-full mt-3 px-3 py-2.5 border border-[#e3c7b9] text-sm outline-none focus:border-[#0c0f3d] font-serif" />
                    </div>

                    <div>
                        <p className="font-sans text-[10px] uppercase tracking-[0.25em] text-[#6d91ad] mb-3">Scope of work</p>
                        <div className="space-y-2">
                            {f.scope_items.map((s, idx) => (
                                <div key={idx} className="grid grid-cols-[1fr_32px] gap-2">
                                    <input placeholder="e.g. 3 workshops + curriculum audit" value={s} onChange={(e) => setScope(idx, e.target.value)} data-testid={`proposal-scope-${idx}`} className="px-3 py-2 border border-[#e3c7b9] text-sm outline-none focus:border-[#0c0f3d]" />
                                    <button type="button" onClick={() => removeScope(idx)} disabled={f.scope_items.length === 1} className="text-[#e47a6a] hover:text-[#0c0f3d] disabled:opacity-30 flex items-center justify-center"><Trash2 size={14} /></button>
                                </div>
                            ))}
                        </div>
                        <button type="button" onClick={addScope} data-testid="proposal-scope-add" className="mt-2 text-xs uppercase tracking-[0.2em] text-[#e47a6a] hover:text-[#0c0f3d] flex items-center gap-1"><Plus size={12} /> Add scope item</button>
                    </div>

                    <div>
                        <p className="font-sans text-[10px] uppercase tracking-[0.25em] text-[#6d91ad] mb-3">Investment</p>
                        <div className="space-y-2">
                            {f.line_items.map((li, idx) => (
                                <div key={idx} className="grid grid-cols-[1fr_100px_80px_32px] gap-2 items-center" data-testid={`proposal-line-${idx}`}>
                                    <input placeholder="Line item description" value={li.description} onChange={(e) => setItem(idx, { description: e.target.value })} data-testid={`proposal-line-desc-${idx}`} className="px-3 py-2 border border-[#e3c7b9] text-sm outline-none focus:border-[#0c0f3d]" />
                                    <input type="number" step="0.01" min="0" placeholder="0.00" value={li.amount} onChange={(e) => setItem(idx, { amount: e.target.value })} data-testid={`proposal-line-amount-${idx}`} className="px-3 py-2 border border-[#e3c7b9] text-sm outline-none focus:border-[#0c0f3d] text-right" />
                                    <input type="number" min="1" step="1" value={li.quantity} onChange={(e) => setItem(idx, { quantity: e.target.value })} data-testid={`proposal-line-qty-${idx}`} className="px-3 py-2 border border-[#e3c7b9] text-sm outline-none focus:border-[#0c0f3d] text-center" />
                                    <button type="button" onClick={() => removeItem(idx)} disabled={f.line_items.length === 1} className="text-[#e47a6a] hover:text-[#0c0f3d] disabled:opacity-30 flex items-center justify-center"><Trash2 size={14} /></button>
                                </div>
                            ))}
                        </div>
                        <button type="button" onClick={addItem} data-testid="proposal-line-add" className="mt-2 text-xs uppercase tracking-[0.2em] text-[#e47a6a] hover:text-[#0c0f3d] flex items-center gap-1"><Plus size={12} /> Add line item</button>
                        <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#e3c7b9]">
                            <label className="flex items-center gap-3">
                                <span className="font-sans text-xs uppercase tracking-[0.2em] text-[#6d91ad]">Deposit</span>
                                <input type="number" min="0" max="100" value={f.deposit_percentage} onChange={(e) => setDeposit(e.target.value)} data-testid="proposal-form-deposit" className="w-20 px-3 py-1.5 border border-[#e3c7b9] text-sm outline-none focus:border-[#0c0f3d] text-right" />
                                <span className="font-sans text-sm text-[#0c0f3d]">%</span>
                            </label>
                            <div className="text-right">
                                <p className="font-sans text-[10px] uppercase tracking-[0.25em] text-[#6d91ad]">Total</p>
                                <p className="font-serif text-2xl text-[#0c0f3d]">{fmtMoney(total)}</p>
                                {deposit > 0 && <p className="font-sans text-xs text-[#e47a6a]">Deposit {fmtMoney(deposit)}</p>}
                            </div>
                        </div>
                    </div>

                    <div>
                        <div className="flex items-center justify-between mb-3">
                            <p className="font-sans text-[10px] uppercase tracking-[0.25em] text-[#6d91ad]">Contract terms</p>
                            {templates.length > 0 && (
                                <select
                                    onChange={(e) => { applyTemplate(e.target.value); e.target.value = ""; }}
                                    data-testid="proposal-form-template-picker"
                                    defaultValue=""
                                    className="text-xs font-sans border border-[#e3c7b9] px-2 py-1.5 outline-none focus:border-[#0c0f3d] max-w-[280px]"
                                >
                                    <option value="">Load from template…</option>
                                    {templates.map((t) => (
                                        <option key={t.id} value={t.id}>{`${t.name} · ${t.category}`}</option>
                                    ))}
                                </select>
                            )}
                        </div>
                        <textarea placeholder="Payment terms, cancellation policy, deliverables timeline, etc." value={f.terms} onChange={(e) => { setF({ ...f, terms: e.target.value }); setTermsTouched(true); }} data-testid="proposal-form-terms" rows={8} className="w-full px-3 py-2.5 border border-[#e3c7b9] text-sm outline-none focus:border-[#0c0f3d] font-serif" />
                        {(f.terms || "").includes("{{") && <p className="font-sans text-xs text-[#e47a6a] mt-2">Merge tokens like <code className="font-mono">{"{{client_name}}"}</code> will be replaced automatically when you send this proposal.</p>}
                    </div>

                    <div>
                        <p className="font-sans text-[10px] uppercase tracking-[0.25em] text-[#6d91ad] mb-1">Expires in (days)</p>
                        <input type="number" min="1" max="180" value={f.expires_in_days} onChange={(e) => setF({ ...f, expires_in_days: e.target.value })} data-testid="proposal-form-expires" className="w-full px-3 py-2.5 border border-[#e3c7b9] text-sm outline-none focus:border-[#0c0f3d]" />
                    </div>
                </div>

                <div className="flex items-center justify-end gap-3 mt-8 pt-6 border-t border-[#e3c7b9]">
                    <button type="button" onClick={onClose} className="px-6 py-2.5 border border-[#0c0f3d] text-[#0c0f3d] text-xs uppercase tracking-[0.2em] hover:bg-[#0c0f3d] hover:text-white">Cancel</button>
                    <button type="submit" disabled={saving} data-testid="proposal-form-submit" className="px-6 py-2.5 bg-[#0c0f3d] text-white text-xs uppercase tracking-[0.2em] hover:bg-[#e47a6a] disabled:opacity-50">
                        {saving ? "Saving…" : isEdit ? "Update proposal" : "Save as draft"}
                    </button>
                </div>
            </form>
        </div>
    );
}

function ProposalDetailDrawer({ id, onClose, onChanged, onEdit }) {
    const [p, setP] = useState(null);
    useEffect(() => {
        axios.get(`${API}/admin/proposals/${id}`, { withCredentials: true }).then((r) => setP(r.data)).catch(() => toast.error("Could not load proposal."));
    }, [id]);

    if (!p) return null;
    return (
        <div className="fixed inset-0 z-40 bg-black/40 flex justify-end" onClick={onClose} data-testid="proposal-detail-drawer">
            <div className="w-full max-w-[560px] bg-white overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="p-8 border-b border-[#e3c7b9]">
                    <div className="flex items-start justify-between">
                        <div>
                            <p className="font-sans text-[10px] uppercase tracking-[0.3em] text-[#e47a6a]">Proposal</p>
                            <h2 className="font-serif text-3xl text-[#0c0f3d] mt-1">{p.title}</h2>
                            <p className="font-sans text-sm text-[#0c0f3d]/60 mt-2">{p.client_name || "—"} · {p.client_email}</p>
                            {p.client_organization && <p className="font-sans text-sm text-[#0c0f3d]/60">{p.client_organization}</p>}
                        </div>
                        <button onClick={onClose} className="p-1 hover:bg-[#e3c7b9]/40"><X size={20} /></button>
                    </div>
                    <div className="mt-4 flex items-center gap-2">
                        <ProposalStatusPill status={p.status} />
                        {p.deposit_percentage > 0 && <span className="text-[10px] uppercase tracking-[0.2em] px-2 py-1 bg-[#ffb5ab]/30 text-[#e47a6a]">Deposit {p.deposit_percentage}%</span>}
                    </div>
                </div>

                <div className="p-8 space-y-6">
                    <div>
                        <p className="font-sans text-[10px] uppercase tracking-[0.25em] text-[#6d91ad] mb-2">Client review link</p>
                        <div className="flex items-center gap-2">
                            <input readOnly value={p.public_url || ""} data-testid="proposal-detail-url" className="flex-1 px-3 py-2 border border-[#e3c7b9] bg-[#fbf9f8] text-xs text-[#0c0f3d]" />
                            <button onClick={() => { navigator.clipboard.writeText(p.public_url); toast.success("Copied."); }} className="p-2 border border-[#e3c7b9] text-[#0c0f3d] hover:bg-[#0c0f3d] hover:text-white"><Copy size={14} /></button>
                            <a
                                href={`${window.location.origin}/proposal/${p.token}`}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="proposal-detail-preview"
                                title="Preview as client"
                                className="p-2 border border-[#e3c7b9] text-[#0c0f3d] hover:bg-[#0c0f3d] hover:text-white"
                            ><ExternalLink size={14} /></a>
                            <a
                                href={`${API}/admin/proposals/${p.id}/pdf`}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="proposal-detail-pdf"
                                title="Download PDF"
                                className="p-2 border border-[#e3c7b9] text-[#0c0f3d] hover:bg-[#0c0f3d] hover:text-white"
                            ><Download size={14} /></a>
                        </div>
                    </div>

                    {p.intro && (
                        <div>
                            <p className="font-sans text-[10px] uppercase tracking-[0.25em] text-[#6d91ad] mb-2">Introduction</p>
                            <p className="font-serif text-base text-[#0c0f3d] leading-[1.7] whitespace-pre-line">{p.intro}</p>
                        </div>
                    )}

                    {p.scope_items?.length > 0 && (
                        <div>
                            <p className="font-sans text-[10px] uppercase tracking-[0.25em] text-[#6d91ad] mb-2">Scope</p>
                            <ul className="space-y-1.5">
                                {p.scope_items.map((s, i) => <li key={i} className="font-sans text-sm text-[#0c0f3d] pl-4 relative before:content-['—'] before:absolute before:left-0 before:text-[#e47a6a]">{s}</li>)}
                            </ul>
                        </div>
                    )}

                    <div>
                        <p className="font-sans text-[10px] uppercase tracking-[0.25em] text-[#6d91ad] mb-2">Investment</p>
                        <div className="border border-[#e3c7b9]">
                            {p.line_items.map((li, i) => (
                                <div key={i} className="grid grid-cols-[1fr_60px_100px] gap-2 p-3 border-b border-[#e3c7b9] last:border-0 items-center">
                                    <p className="font-sans text-sm text-[#0c0f3d]">{li.description}</p>
                                    <p className="font-sans text-xs text-[#0c0f3d]/60 text-center">× {li.quantity}</p>
                                    <p className="font-serif text-sm text-[#0c0f3d] text-right">{fmtMoney((li.amount * li.quantity))}</p>
                                </div>
                            ))}
                            <div className="p-3 bg-[#fbf9f8] flex justify-between items-center">
                                <p className="font-sans text-[10px] uppercase tracking-[0.25em] text-[#0c0f3d]">Total</p>
                                <p className="font-serif text-xl text-[#0c0f3d]">{fmtMoney(p.total_amount)}</p>
                            </div>
                            {p.deposit_percentage > 0 && (
                                <div className="p-3 bg-[#ffb5ab]/20 flex justify-between items-center border-t border-[#e3c7b9]">
                                    <p className="font-sans text-[10px] uppercase tracking-[0.25em] text-[#e47a6a]">Deposit ({p.deposit_percentage}%)</p>
                                    <p className="font-serif text-lg text-[#e47a6a]">{fmtMoney(p.deposit_amount)}</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {p.terms && (
                        <div>
                            <p className="font-sans text-[10px] uppercase tracking-[0.25em] text-[#6d91ad] mb-2">Terms (preview)</p>
                            <p className="font-serif text-sm text-[#0c0f3d]/80 leading-[1.7] whitespace-pre-line">{p.terms_rendered || p.terms}</p>
                            {p.terms.includes("{{") && (
                                <p className="font-sans text-[10px] text-[#e47a6a] mt-2 italic">Tokens are shown filled with the client&rsquo;s data. Recipients always see the rendered version.</p>
                            )}
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-3 pt-4 border-t border-[#e3c7b9]">
                        <MiniStat label="Sent" value={p.sent_at ? new Date(p.sent_at).toLocaleDateString() : "—"} />
                        <MiniStat label="Viewed" value={p.viewed_at ? new Date(p.viewed_at).toLocaleDateString() : "—"} />
                        <MiniStat label="Expires" value={p.expires_at ? new Date(p.expires_at).toLocaleDateString() : "—"} />
                        <MiniStat label={p.status === "accepted" ? "Accepted" : "Status"} value={p.status === "accepted" && p.accepted_at ? new Date(p.accepted_at).toLocaleDateString() : p.status} />
                    </div>

                    {p.status === "accepted" && p.signature_name && (
                        <div className="bg-[#4a7c59]/10 border border-[#4a7c59]/30 p-4">
                            <p className="font-sans text-[10px] uppercase tracking-[0.25em] text-[#4a7c59]">Signed</p>
                            <p className="font-serif text-lg text-[#0c0f3d] mt-1">{p.signature_name}</p>
                            <p className="font-sans text-xs text-[#0c0f3d]/60 mt-1">{new Date(p.accepted_at).toLocaleString()}</p>
                            {p.acceptance_notes && <p className="font-serif text-sm text-[#0c0f3d]/80 mt-2 italic">&ldquo;{p.acceptance_notes}&rdquo;</p>}
                            {p.linked_invoice_id && (
                                <p className="font-sans text-xs text-[#4a7c59] mt-3 flex items-center gap-1"><FileText size={12} /> Invoice generated (see Invoices tab)</p>
                            )}
                        </div>
                    )}
                    {p.status === "declined" && p.decline_reason && (
                        <div className="bg-[#e47a6a]/10 border border-[#e47a6a]/30 p-4">
                            <p className="font-sans text-[10px] uppercase tracking-[0.25em] text-[#e47a6a]">Client declined</p>
                            <p className="font-serif text-sm text-[#0c0f3d]/80 mt-2 italic">&ldquo;{p.decline_reason}&rdquo;</p>
                        </div>
                    )}

                    {["draft", "sent", "viewed"].includes(p.status) && (
                        <div className="pt-4 border-t border-[#e3c7b9]">
                            <button onClick={() => onEdit(p)} data-testid="proposal-detail-edit" className="px-4 py-2 border border-[#0c0f3d] text-[#0c0f3d] text-xs uppercase tracking-[0.2em] hover:bg-[#0c0f3d] hover:text-white flex items-center gap-2"><PenSquare size={12} /> Edit proposal</button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}


// ---------- Shared ----------
function PanelHeader({ eyebrow, title }) {
    return (
        <div>
            <p className="font-sans text-[10px] uppercase tracking-[0.3em] text-[#e47a6a]">{eyebrow}</p>
            <h1 className="font-serif text-4xl md:text-5xl text-[#0c0f3d] mt-2">{title}</h1>
        </div>
    );
}
function SectionCard({ title, children, testId }) {
    return (
        <div className="bg-white border border-[#e3c7b9] p-6" data-testid={testId}>
            <p className="font-sans text-[10px] uppercase tracking-[0.25em] text-[#6d91ad] mb-4">{title}</p>
            <div>{children}</div>
        </div>
    );
}
function EmptyMsg({ children }) {
    return <p className="font-serif italic text-sm text-[#0c0f3d]/60 py-4">{children}</p>;
}
function StagePill({ stage }) {
    const colors = {
        "New Lead": "bg-[#6d91ad]/20 text-[#6d91ad]",
        "Discovery Call": "bg-[#30b8cf]/20 text-[#30b8cf]",
        "Proposal Sent": "bg-[#b59b7c]/20 text-[#b59b7c]",
        "Signed": "bg-[#ffb5ab]/40 text-[#e47a6a]",
        "Client": "bg-[#e47a6a]/20 text-[#e47a6a]",
        "Complete": "bg-[#0c0f3d]/10 text-[#0c0f3d]",
        "Archived": "bg-black/10 text-black/50",
    };
    return <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 ${colors[stage] || "bg-gray-100"}`}>{stage}</span>;
}
function BookingStatusPill({ status }) {
    const map = {
        confirmed: { label: "Confirmed", cls: "bg-[#4a7c59]/20 text-[#4a7c59]" },
        paid: { label: "Paid", cls: "bg-[#4a7c59]/20 text-[#4a7c59]" },
        pending: { label: "Pending Payment", cls: "bg-[#b59b7c]/20 text-[#b59b7c]" },
        cancelled: { label: "Cancelled", cls: "bg-black/10 text-black/50" },
    };
    const m = map[status] || { label: status, cls: "bg-gray-100" };
    return <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 ${m.cls}`}>{m.label}</span>;
}
function Th({ children }) {
    return <th className="text-left font-sans text-[10px] uppercase tracking-[0.25em] text-[#6d91ad] px-4 py-3">{children}</th>;
}
function Td({ children }) {
    return <td className="px-4 py-3">{children}</td>;
}
function MiniStat({ label, value }) {
    return (
        <div className="bg-[#fbf9f8] p-4 border border-[#e3c7b9]">
            <p className="font-sans text-[10px] uppercase tracking-[0.25em] text-[#6d91ad]">{label}</p>
            <p className="font-serif text-2xl text-[#0c0f3d] mt-2">{value}</p>
        </div>
    );
}
